import { useState, useEffect, useRef } from 'react';
import {
  Calculator, X, GripVertical, Ruler, Hash, Box, 
  Home, Zap, DollarSign, PaintBucket,
  Layers, Grid3X3, ArrowLeftRight, Minimize2, Maximize2,
  Square, Accessibility, Palette, Sofa, Scissors,
  Shovel, Wind, Mountain, Package, Clock, FileText,
  Droplets, Thermometer, Fence, Hammer, Footprints, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

// ============================================
// DRAGGABLE FLOATING CARD COMPONENT
// ============================================

function DraggableCard({ id, title, icon: Icon, children, onClose, darkMode, initialPosition }) {
  const cardRef = useRef(null);
  const [position, setPosition] = useState(initialPosition || { x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);

  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    const rect = cardRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // On mobile, center the card and make it full-width
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  return (
    <div
      ref={cardRef}
      className={`fixed z-50 ${isDragging ? 'cursor-grabbing' : ''} ${isMobile ? 'left-2 right-2' : ''}`}
      style={isMobile ? { top: position.y, left: 8, right: 8 } : { left: position.x, top: position.y }}
      data-testid={`calculator-card-${id}`}
    >
      <Card className={`shadow-2xl ${isMobile ? 'w-full' : 'w-80'} max-w-[calc(100vw-16px)] ${darkMode ? 'bg-[#1A1A1A] border-[#3A3A3A]' : 'bg-white border-gray-200'}`}>
        <CardHeader 
          className={`py-2 px-3 cursor-grab flex flex-row items-center justify-between ${
            darkMode ? 'bg-[#2A2A2A] border-b border-[#3A3A3A]' : 'bg-gray-50 border-b'
          }`}
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <GripVertical className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <Icon className="w-4 h-4 text-[#BC5D41]" />
            <CardTitle className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{title}</CardTitle>
          </div>
          <div className="flex items-center gap-1 no-drag">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 hover:bg-red-100 hover:text-red-600"
              onClick={onClose}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </CardHeader>
        {!isMinimized && (
          <CardContent className="p-3 no-drag">
            {children}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

// ============================================
// INDIVIDUAL CALCULATOR COMPONENTS
// ============================================

// Inches to Metric Converter
function InchesConverter({ darkMode }) {
  const [inches, setInches] = useState('');
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (inches && !isNaN(parseFloat(inches))) {
      const val = parseFloat(inches);
      setResults({
        cm: (val * 2.54).toFixed(4),
        mm: (val * 25.4).toFixed(3),
        meters: (val * 0.0254).toFixed(5),
        feet: (val / 12).toFixed(4),
      });
    } else {
      setResults(null);
    }
  }, [inches]);

  return (
    <div className="space-y-3">
      <div>
        <Label className={darkMode ? 'text-gray-300' : ''}>Inches</Label>
        <Input
          type="number"
          step="any"
          value={inches}
          onChange={(e) => setInches(e.target.value)}
          placeholder="Enter inches"
          className={darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}
        />
      </div>
      {results && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Centimeters</p>
            <p className={`font-mono text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{results.cm} cm</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Millimeters</p>
            <p className={`font-mono text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{results.mm} mm</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Meters</p>
            <p className={`font-mono text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{results.meters} m</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Feet</p>
            <p className={`font-mono text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{results.feet} ft</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Decimal to Fraction Converter
function DecimalToFraction({ darkMode }) {
  const [decimal, setDecimal] = useState('');
  const [precision, setPrecision] = useState('16');
  const [result, setResult] = useState(null);

  const gcd = (a, b) => b ? gcd(b, a % b) : a;

  const decimalToFraction = (dec, maxDenom) => {
    if (dec === 0) return { whole: 0, num: 0, den: 1 };
    
    const whole = Math.floor(Math.abs(dec));
    const remainder = Math.abs(dec) - whole;
    
    let bestNum = 0;
    let bestDen = 1;
    let bestError = remainder;
    
    for (let den = 1; den <= maxDenom; den++) {
      const num = Math.round(remainder * den);
      const error = Math.abs(remainder - num / den);
      if (error < bestError) {
        bestError = error;
        bestNum = num;
        bestDen = den;
      }
    }
    
    const divisor = gcd(bestNum, bestDen);
    return {
      whole: dec < 0 ? -whole : whole,
      num: bestNum / divisor,
      den: bestDen / divisor,
      sign: dec < 0 ? '-' : ''
    };
  };

  useEffect(() => {
    if (decimal && !isNaN(parseFloat(decimal))) {
      const val = parseFloat(decimal);
      const frac = decimalToFraction(val, parseInt(precision));
      
      let display = '';
      if (frac.whole !== 0 && frac.num !== 0) {
        display = `${frac.sign}${Math.abs(frac.whole)} ${frac.num}/${frac.den}`;
      } else if (frac.whole !== 0) {
        display = `${frac.whole}`;
      } else if (frac.num !== 0) {
        display = `${frac.sign}${frac.num}/${frac.den}`;
      } else {
        display = '0';
      }
      
      let inchDisplay = frac.whole !== 0 ? `${frac.whole}"` : '';
      if (frac.num !== 0) {
        inchDisplay = frac.whole !== 0 
          ? `${Math.abs(frac.whole)} ${frac.num}/${frac.den}"` 
          : `${frac.num}/${frac.den}"`;
      }
      
      setResult({ fraction: display, inches: inchDisplay || '0"' });
    } else {
      setResult(null);
    }
  }, [decimal, precision]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Decimal</Label>
          <Input
            type="number"
            step="any"
            value={decimal}
            onChange={(e) => setDecimal(e.target.value)}
            placeholder="e.g., 3.625"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}
          />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Precision</Label>
          <Select value={precision} onValueChange={setPrecision}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">1/2"</SelectItem>
              <SelectItem value="4">1/4"</SelectItem>
              <SelectItem value="8">1/8"</SelectItem>
              <SelectItem value="16">1/16"</SelectItem>
              <SelectItem value="32">1/32"</SelectItem>
              <SelectItem value="64">1/64"</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>
            {result.fraction}
          </p>
          <p className={`text-sm font-mono mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {result.inches}
          </p>
        </div>
      )}
    </div>
  );
}

// Concrete Volume Calculator
function ConcreteCalculator({ darkMode }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState('');
  const [unit, setUnit] = useState('feet');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (length && width && depth) {
      let l = parseFloat(length);
      let w = parseFloat(width);
      let d = parseFloat(depth);
      
      if (unit === 'inches') { l /= 12; w /= 12; d /= 12; }
      else if (unit === 'meters') { l *= 3.28084; w *= 3.28084; d *= 3.28084; }
      
      const cubicFeet = l * w * d;
      const cubicYards = cubicFeet / 27;
      
      setResult({
        cubicFeet: cubicFeet.toFixed(2),
        cubicYards: cubicYards.toFixed(2),
        bags60lb: Math.ceil(cubicYards * 45),
        bags80lb: Math.ceil(cubicYards * 33),
      });
    } else {
      setResult(null);
    }
  }, [length, width, depth, unit]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Length</Label>
          <Input type="number" step="any" value={length} onChange={(e) => setLength(e.target.value)} 
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Width</Label>
          <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Depth</Label>
          <Input type="number" step="any" value={depth} onChange={(e) => setDepth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <Select value={unit} onValueChange={setUnit}>
        <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="feet">Feet</SelectItem>
          <SelectItem value="inches">Inches</SelectItem>
          <SelectItem value="meters">Meters</SelectItem>
        </SelectContent>
      </Select>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Cubic Yards</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.cubicYards}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Cubic Feet</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.cubicFeet}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>60lb Bags</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.bags60lb}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>80lb Bags</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.bags80lb}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Block Calculator
function BlockCalculator({ darkMode }) {
  const [wallLength, setWallLength] = useState('');
  const [wallHeight, setWallHeight] = useState('');
  const [wasteFactor, setWasteFactor] = useState('10');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (wallLength && wallHeight) {
      const sqFt = parseFloat(wallLength) * parseFloat(wallHeight);
      const blocksNeeded = sqFt * 1.125;
      const waste = parseFloat(wasteFactor) / 100;
      const totalBlocks = Math.ceil(blocksNeeded * (1 + waste));
      
      setResult({ sqFt: sqFt.toFixed(1), blocks: totalBlocks, mortarBags: Math.ceil(totalBlocks / 30) });
    } else {
      setResult(null);
    }
  }, [wallLength, wallHeight, wasteFactor]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Length (ft)</Label>
          <Input type="number" step="any" value={wallLength} onChange={(e) => setWallLength(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Height (ft)</Label>
          <Input type="number" step="any" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Waste %</Label>
        <Input type="number" value={wasteFactor} onChange={(e) => setWasteFactor(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`grid grid-cols-3 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Sq Ft</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.sqFt}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Blocks</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.blocks}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Mortar</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.mortarBags} bags</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Brick Calculator
function BrickCalculator({ darkMode }) {
  const [sqFt, setSqFt] = useState('');
  const [wasteFactor, setWasteFactor] = useState('5');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (sqFt) {
      const area = parseFloat(sqFt);
      const bricksNeeded = area * 6.75; // modular bricks
      const waste = parseFloat(wasteFactor) / 100;
      const totalBricks = Math.ceil(bricksNeeded * (1 + waste));
      
      setResult({ bricks: totalBricks, mortarBags: Math.ceil(totalBricks / 500) });
    } else {
      setResult(null);
    }
  }, [sqFt, wasteFactor]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Area (sq ft)</Label>
        <Input type="number" step="any" value={sqFt} onChange={(e) => setSqFt(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Waste %</Label>
        <Input type="number" value={wasteFactor} onChange={(e) => setWasteFactor(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Bricks</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.bricks}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Mortar Bags</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.mortarBags}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Board Foot Calculator
function BoardFootCalculator({ darkMode }) {
  const [thickness, setThickness] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (thickness && width && length) {
      const bf = (parseFloat(thickness) * parseFloat(width) * parseFloat(length)) / 144;
      const totalBf = bf * (parseInt(quantity) || 1);
      setResult({ boardFeet: bf.toFixed(2), totalBoardFeet: totalBf.toFixed(2) });
    } else {
      setResult(null);
    }
  }, [thickness, width, length, quantity]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Thickness (in)</Label>
          <Input type="number" step="any" value={thickness} onChange={(e) => setThickness(e.target.value)}
            placeholder="2" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Width (in)</Label>
          <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
            placeholder="6" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Length (in)</Label>
          <Input type="number" step="any" value={length} onChange={(e) => setLength(e.target.value)}
            placeholder="96" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Quantity</Label>
          <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Per Board</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.boardFeet} BF</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Total</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.totalBoardFeet} BF</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Stud Calculator
function StudCalculator({ darkMode }) {
  const [wallLength, setWallLength] = useState('');
  const [wallHeight, setWallHeight] = useState('');
  const [spacing, setSpacing] = useState('16');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (wallLength && wallHeight) {
      const studs = Math.ceil((parseFloat(wallLength) * 12) / parseInt(spacing)) + 5;
      const platesNeeded = Math.ceil((parseFloat(wallLength) * 3) / 8);
      const h = parseFloat(wallHeight);
      const studLength = h <= 8 ? '92⅝"' : h <= 9 ? '104⅝"' : `${Math.ceil(h * 12 - 4.5)}"`;
      setResult({ studs, plates: platesNeeded, studLength });
    } else {
      setResult(null);
    }
  }, [wallLength, wallHeight, spacing]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Length (ft)</Label>
          <Input type="number" step="any" value={wallLength} onChange={(e) => setWallLength(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Height (ft)</Label>
          <Input type="number" step="any" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Spacing (in O.C.)</Label>
        <Input 
          type="number" 
          step="any" 
          value={spacing} 
          onChange={(e) => setSpacing(e.target.value)}
          placeholder="e.g., 16"
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} 
        />
      </div>
      {result && (
        <div className={`grid grid-cols-3 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Studs</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.studs}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Plates</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.plates}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Length</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.studLength}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Roof Pitch Calculator
function RoofPitchCalculator({ darkMode }) {
  const [rise, setRise] = useState('');
  const [run, setRun] = useState('12');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (rise && run) {
      const r = parseFloat(rise);
      const u = parseFloat(run);
      const angle = Math.atan(r / u) * (180 / Math.PI);
      const slope = (r / u) * 100;
      const multiplier = Math.sqrt((r * r) + (u * u)) / u;
      setResult({ pitch: `${r}/${u}`, angle: angle.toFixed(1), slope: slope.toFixed(1), multiplier: multiplier.toFixed(3) });
    } else {
      setResult(null);
    }
  }, [rise, run]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Rise (in)</Label>
          <Input type="number" step="any" value={rise} onChange={(e) => setRise(e.target.value)}
            placeholder="6" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Run (in)</Label>
          <Input type="number" step="any" value={run} onChange={(e) => setRun(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Pitch</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.pitch}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Angle</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.angle}°</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Slope</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.slope}%</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Multiplier</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.multiplier}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Roofing Calculator  
function RoofingCalculator({ darkMode }) {
  const [sqFt, setSqFt] = useState('');
  const [pitch, setPitch] = useState('6');
  const [wasteFactor, setWasteFactor] = useState('10');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (sqFt) {
      const area = parseFloat(sqFt);
      const multipliers = { 0: 1, 1: 1.003, 2: 1.014, 3: 1.031, 4: 1.054, 5: 1.083, 6: 1.118, 7: 1.158, 8: 1.202, 9: 1.25, 10: 1.302, 11: 1.357, 12: 1.414 };
      const actualArea = area * (multipliers[parseInt(pitch)] || 1);
      const squares = actualArea / 100;
      const totalSquares = squares * (1 + parseFloat(wasteFactor) / 100);
      setResult({ actualSqFt: actualArea.toFixed(0), squares: squares.toFixed(2), totalSquares: totalSquares.toFixed(2), bundles: Math.ceil(totalSquares * 3) });
    } else {
      setResult(null);
    }
  }, [sqFt, pitch, wasteFactor]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Footprint Area (sq ft)</Label>
        <Input type="number" step="any" value={sqFt} onChange={(e) => setSqFt(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Pitch</Label>
          <Select value={pitch} onValueChange={setPitch}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0,1,2,3,4,5,6,7,8,9,10,11,12].map(p => (
                <SelectItem key={p} value={p.toString()}>{p}/12</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Waste %</Label>
          <Input type="number" value={wasteFactor} onChange={(e) => setWasteFactor(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Squares</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.totalSquares}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Bundles</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.bundles}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Drywall Calculator
function DrywallCalculator({ darkMode }) {
  const [sqFt, setSqFt] = useState('');
  const [wasteFactor, setWasteFactor] = useState('10');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (sqFt) {
      const area = parseFloat(sqFt);
      const sheets = Math.ceil((area * (1 + parseFloat(wasteFactor) / 100)) / 32);
      setResult({ sheets, screwBoxes: Math.ceil(sheets * 48 / 750), mudGallons: Math.ceil(area / 100), tapeRolls: Math.ceil(area / 475) });
    } else {
      setResult(null);
    }
  }, [sqFt, wasteFactor]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall/Ceiling Area (sq ft)</Label>
        <Input type="number" step="any" value={sqFt} onChange={(e) => setSqFt(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Waste %</Label>
        <Input type="number" value={wasteFactor} onChange={(e) => setWasteFactor(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`grid grid-cols-2 gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>4×8 Sheets</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.sheets}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Screw Boxes</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.screwBoxes}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Mud (gal)</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.mudGallons}</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Tape Rolls</p>
            <p className={`font-mono text-sm ${darkMode ? 'text-white' : ''}`}>{result.tapeRolls}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Paint Calculator
function PaintCalculator({ darkMode }) {
  const [sqFt, setSqFt] = useState('');
  const [coats, setCoats] = useState('2');
  const [coverage, setCoverage] = useState('350');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (sqFt) {
      const totalSqFt = parseFloat(sqFt) * parseInt(coats);
      const gallons = totalSqFt / parseFloat(coverage);
      setResult({ totalSqFt: totalSqFt.toFixed(0), gallons: gallons.toFixed(2), gallonsRounded: Math.ceil(gallons) });
    } else {
      setResult(null);
    }
  }, [sqFt, coats, coverage]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Surface Area (sq ft)</Label>
        <Input type="number" step="any" value={sqFt} onChange={(e) => setSqFt(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Number of Coats</Label>
          <Input 
            type="number" 
            step="1" 
            min="1"
            value={coats} 
            onChange={(e) => setCoats(e.target.value)}
            placeholder="e.g., 2"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} 
          />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Coverage (sq ft/gal)</Label>
          <Input type="number" value={coverage} onChange={(e) => setCoverage(e.target.value)}
            placeholder="350" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg text-center ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.gallonsRounded} gal</p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Exact: {result.gallons} gal</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// FINANCIAL CALCULATORS
// ============================================

// Markup & Margin Calculator
function MarkupMarginCalculator({ darkMode }) {
  const [cost, setCost] = useState('');
  const [markupPercent, setMarkupPercent] = useState('');
  const [marginPercent, setMarginPercent] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [mode, setMode] = useState('markup'); // 'markup', 'margin', 'reverse'
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!cost) { setResult(null); return; }
    const costVal = parseFloat(cost);
    
    if (mode === 'markup' && markupPercent) {
      const markup = parseFloat(markupPercent) / 100;
      const price = costVal * (1 + markup);
      const margin = (price - costVal) / price * 100;
      const profit = price - costVal;
      setResult({ sellingPrice: price.toFixed(2), margin: margin.toFixed(2), profit: profit.toFixed(2) });
    } else if (mode === 'margin' && marginPercent) {
      const margin = parseFloat(marginPercent) / 100;
      const price = costVal / (1 - margin);
      const markup = ((price - costVal) / costVal) * 100;
      const profit = price - costVal;
      setResult({ sellingPrice: price.toFixed(2), markup: markup.toFixed(2), profit: profit.toFixed(2) });
    } else if (mode === 'reverse' && sellingPrice) {
      const price = parseFloat(sellingPrice);
      const profit = price - costVal;
      const markup = (profit / costVal) * 100;
      const margin = (profit / price) * 100;
      setResult({ markup: markup.toFixed(2), margin: margin.toFixed(2), profit: profit.toFixed(2) });
    } else {
      setResult(null);
    }
  }, [cost, markupPercent, marginPercent, sellingPrice, mode]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Mode</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="markup">From Markup %</SelectItem>
            <SelectItem value="margin">From Margin %</SelectItem>
            <SelectItem value="reverse">From Selling Price</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Cost ($)</Label>
        <Input type="number" step="any" value={cost} onChange={(e) => setCost(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {mode === 'markup' && (
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Markup %</Label>
          <Input type="number" step="any" value={markupPercent} onChange={(e) => setMarkupPercent(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      )}
      {mode === 'margin' && (
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Margin %</Label>
          <Input type="number" step="any" value={marginPercent} onChange={(e) => setMarginPercent(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      )}
      {mode === 'reverse' && (
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Selling Price ($)</Label>
          <Input type="number" step="any" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      )}
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {mode === 'markup' && (
            <>
              <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.sellingPrice}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Margin: {result.margin}% | Profit: ${result.profit}</p>
            </>
          )}
          {mode === 'margin' && (
            <>
              <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.sellingPrice}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Markup: {result.markup}% | Profit: ${result.profit}</p>
            </>
          )}
          {mode === 'reverse' && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Markup</p>
                <p className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.markup}%</p>
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Margin</p>
                <p className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.margin}%</p>
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Profit</p>
                <p className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.profit}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Labor Burden Calculator
function LaborBurdenCalculator({ darkMode }) {
  const [hourlyRate, setHourlyRate] = useState('');
  const [payrollTax, setPayrollTax] = useState('7.65'); // FICA
  const [workersComp, setWorkersComp] = useState('5');
  const [healthIns, setHealthIns] = useState('');
  const [pto, setPto] = useState('4'); // weeks
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!hourlyRate) { setResult(null); return; }
    const rate = parseFloat(hourlyRate);
    const annualGross = rate * 2080; // 52 weeks × 40 hrs
    
    const ficaAmt = annualGross * (parseFloat(payrollTax) / 100);
    const wcAmt = annualGross * (parseFloat(workersComp) / 100);
    const healthAmt = healthIns ? parseFloat(healthIns) * 12 : 0;
    const ptoWeeks = parseFloat(pto);
    const ptoAmt = rate * 40 * ptoWeeks;
    
    const totalBurden = ficaAmt + wcAmt + healthAmt + ptoAmt;
    const burdenPercent = (totalBurden / annualGross) * 100;
    const trueHourly = (annualGross + totalBurden) / 2080;
    
    setResult({
      annualGross: annualGross.toFixed(0),
      totalBurden: totalBurden.toFixed(0),
      burdenPercent: burdenPercent.toFixed(1),
      trueHourly: trueHourly.toFixed(2),
      breakdown: {
        fica: ficaAmt.toFixed(0),
        wc: wcAmt.toFixed(0),
        health: healthAmt.toFixed(0),
        pto: ptoAmt.toFixed(0)
      }
    });
  }, [hourlyRate, payrollTax, workersComp, healthIns, pto]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Hourly Rate ($)</Label>
        <Input type="number" step="any" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Payroll Tax %</Label>
          <Input type="number" step="any" value={payrollTax} onChange={(e) => setPayrollTax(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Workers Comp %</Label>
          <Input type="number" step="any" value={workersComp} onChange={(e) => setWorkersComp(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Health Ins ($/mo)</Label>
          <Input type="number" step="any" value={healthIns} onChange={(e) => setHealthIns(e.target.value)}
            placeholder="Optional" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>PTO (weeks)</Label>
          <Input type="number" step="any" value={pto} onChange={(e) => setPto(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="text-center mb-2">
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>True Hourly Cost</p>
            <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.trueHourly}/hr</p>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Burden: {result.burdenPercent}%</p>
          </div>
          <div className={`text-xs grid grid-cols-4 gap-1 pt-2 border-t ${darkMode ? 'border-[#3A3A3A]' : 'border-gray-200'}`}>
            <div className="text-center">
              <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>FICA</p>
              <p className={darkMode ? 'text-gray-300' : ''}>${result.breakdown.fica}</p>
            </div>
            <div className="text-center">
              <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>WC</p>
              <p className={darkMode ? 'text-gray-300' : ''}>${result.breakdown.wc}</p>
            </div>
            <div className="text-center">
              <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Health</p>
              <p className={darkMode ? 'text-gray-300' : ''}>${result.breakdown.health}</p>
            </div>
            <div className="text-center">
              <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>PTO</p>
              <p className={darkMode ? 'text-gray-300' : ''}>${result.breakdown.pto}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Overhead Allocation Calculator
function OverheadAllocationCalculator({ darkMode }) {
  const [monthlyOverhead, setMonthlyOverhead] = useState('');
  const [numProjects, setNumProjects] = useState('');
  const [projectValue, setProjectValue] = useState('');
  const [totalRevenue, setTotalRevenue] = useState('');
  const [method, setMethod] = useState('equal'); // 'equal', 'revenue'
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!monthlyOverhead) { setResult(null); return; }
    const overhead = parseFloat(monthlyOverhead);
    const annual = overhead * 12;
    
    if (method === 'equal' && numProjects) {
      const projects = parseInt(numProjects);
      const perProject = annual / projects;
      const monthly = perProject / 12;
      setResult({ annual, perProject: perProject.toFixed(2), monthly: monthly.toFixed(2), method: 'Equal Split' });
    } else if (method === 'revenue' && projectValue && totalRevenue) {
      const projVal = parseFloat(projectValue);
      const totalRev = parseFloat(totalRevenue);
      const percent = (projVal / totalRev) * 100;
      const allocation = (projVal / totalRev) * annual;
      setResult({ annual, allocation: allocation.toFixed(2), percent: percent.toFixed(1), method: 'Revenue Based' });
    } else {
      setResult(null);
    }
  }, [monthlyOverhead, numProjects, projectValue, totalRevenue, method]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Monthly Overhead ($)</Label>
        <Input type="number" step="any" value={monthlyOverhead} onChange={(e) => setMonthlyOverhead(e.target.value)}
          placeholder="Rent, utilities, admin..." className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Allocation Method</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="equal">Equal Split</SelectItem>
            <SelectItem value="revenue">Revenue Based</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {method === 'equal' && (
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}># Active Projects</Label>
          <Input type="number" value={numProjects} onChange={(e) => setNumProjects(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      )}
      {method === 'revenue' && (
        <>
          <div>
            <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Project Value ($)</Label>
            <Input type="number" step="any" value={projectValue} onChange={(e) => setProjectValue(e.target.value)}
              className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          </div>
          <div>
            <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Total Annual Revenue ($)</Label>
            <Input type="number" step="any" value={totalRevenue} onChange={(e) => setTotalRevenue(e.target.value)}
              className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          </div>
        </>
      )}
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{result.method}</p>
          {method === 'equal' ? (
            <>
              <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.perProject}/project</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>${result.monthly}/month per project</p>
            </>
          ) : (
            <>
              <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.allocation}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{result.percent}% of overhead</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Break-Even Calculator
function BreakEvenCalculator({ darkMode }) {
  const [fixedCosts, setFixedCosts] = useState('');
  const [avgJobPrice, setAvgJobPrice] = useState('');
  const [avgJobCost, setAvgJobCost] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!fixedCosts || !avgJobPrice || !avgJobCost) { setResult(null); return; }
    const fixed = parseFloat(fixedCosts);
    const price = parseFloat(avgJobPrice);
    const cost = parseFloat(avgJobCost);
    
    const contributionMargin = price - cost;
    const marginPercent = (contributionMargin / price) * 100;
    const breakEvenRevenue = fixed / (marginPercent / 100);
    const breakEvenJobs = fixed / contributionMargin;
    
    setResult({
      revenue: breakEvenRevenue.toFixed(0),
      jobs: Math.ceil(breakEvenJobs),
      contributionMargin: contributionMargin.toFixed(2),
      marginPercent: marginPercent.toFixed(1)
    });
  }, [fixedCosts, avgJobPrice, avgJobCost]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Annual Fixed Costs ($)</Label>
        <Input type="number" step="any" value={fixedCosts} onChange={(e) => setFixedCosts(e.target.value)}
          placeholder="Rent, salaries, insurance..." className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Avg Job Price ($)</Label>
        <Input type="number" step="any" value={avgJobPrice} onChange={(e) => setAvgJobPrice(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Avg Job Cost ($)</Label>
        <Input type="number" step="any" value={avgJobCost} onChange={(e) => setAvgJobCost(e.target.value)}
          placeholder="Materials + labor" className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Break-Even Revenue</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${parseInt(result.revenue).toLocaleString()}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Jobs Needed</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.jobs}</p>
            </div>
          </div>
          <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Contribution Margin: ${result.contributionMargin} ({result.marginPercent}%)
          </p>
        </div>
      )}
    </div>
  );
}

// Retainage Calculator
function RetainageCalculator({ darkMode }) {
  const [contractAmount, setContractAmount] = useState('');
  const [retainagePercent, setRetainagePercent] = useState('10');
  const [percentComplete, setPercentComplete] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!contractAmount || !percentComplete) { setResult(null); return; }
    const contract = parseFloat(contractAmount);
    const retPct = parseFloat(retainagePercent) / 100;
    const complete = parseFloat(percentComplete) / 100;
    
    const earned = contract * complete;
    const retainage = earned * retPct;
    const netPayable = earned - retainage;
    const totalRetained = contract * retPct; // At 100%
    
    setResult({
      earned: earned.toFixed(2),
      retainage: retainage.toFixed(2),
      netPayable: netPayable.toFixed(2),
      totalRetained: totalRetained.toFixed(2)
    });
  }, [contractAmount, retainagePercent, percentComplete]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Contract Amount ($)</Label>
        <Input type="number" step="any" value={contractAmount} onChange={(e) => setContractAmount(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Retainage %</Label>
          <Input type="number" step="any" value={retainagePercent} onChange={(e) => setRetainagePercent(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>% Complete</Label>
          <Input type="number" step="any" value={percentComplete} onChange={(e) => setPercentComplete(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Earned</p>
              <p className={`font-mono ${darkMode ? 'text-white' : ''}`}>${parseFloat(result.earned).toLocaleString()}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Retained</p>
              <p className={`font-mono text-orange-600`}>${parseFloat(result.retainage).toLocaleString()}</p>
            </div>
          </div>
          <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-[#3A3A3A]' : 'border-gray-200'}`}>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Net Payable</p>
            <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${parseFloat(result.netPayable).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Cost per Square Foot Calculator
function CostPerSqFtCalculator({ darkMode }) {
  const [totalCost, setTotalCost] = useState('');
  const [squareFeet, setSquareFeet] = useState('');
  const [benchmarkLow, setBenchmarkLow] = useState('150');
  const [benchmarkHigh, setBenchmarkHigh] = useState('300');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!totalCost || !squareFeet) { setResult(null); return; }
    const cost = parseFloat(totalCost);
    const sqft = parseFloat(squareFeet);
    const perSqFt = cost / sqft;
    const low = parseFloat(benchmarkLow);
    const high = parseFloat(benchmarkHigh);
    
    let status = 'average';
    if (perSqFt < low) status = 'below';
    else if (perSqFt > high) status = 'above';
    
    setResult({
      perSqFt: perSqFt.toFixed(2),
      status,
      percentOfHigh: ((perSqFt / high) * 100).toFixed(0)
    });
  }, [totalCost, squareFeet, benchmarkLow, benchmarkHigh]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Total Project Cost ($)</Label>
        <Input type="number" step="any" value={totalCost} onChange={(e) => setTotalCost(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Square Footage</Label>
        <Input type="number" step="any" value={squareFeet} onChange={(e) => setSquareFeet(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Benchmark Low</Label>
          <Input type="number" step="any" value={benchmarkLow} onChange={(e) => setBenchmarkLow(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Benchmark High</Label>
          <Input type="number" step="any" value={benchmarkHigh} onChange={(e) => setBenchmarkHigh(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-2xl font-mono font-bold text-center ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.perSqFt}/SF</p>
          <div className={`mt-2 flex items-center justify-center gap-2`}>
            <Badge className={
              result.status === 'below' ? 'bg-green-100 text-green-700' :
              result.status === 'above' ? 'bg-red-100 text-red-700' :
              'bg-blue-100 text-blue-700'
            }>
              {result.status === 'below' ? 'Below Benchmark' :
               result.status === 'above' ? 'Above Benchmark' : 'Within Range'}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}

// Escalation Rate Calculator
function EscalationCalculator({ darkMode }) {
  const [baseCost, setBaseCost] = useState('');
  const [escalationRate, setEscalationRate] = useState('4');
  const [months, setMonths] = useState('12');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!baseCost) { setResult(null); return; }
    const base = parseFloat(baseCost);
    const rate = parseFloat(escalationRate) / 100;
    const period = parseInt(months);
    
    // Monthly compounding
    const monthlyRate = rate / 12;
    const escalated = base * Math.pow(1 + monthlyRate, period);
    const increase = escalated - base;
    const percentIncrease = ((escalated - base) / base) * 100;
    
    setResult({
      escalated: escalated.toFixed(2),
      increase: increase.toFixed(2),
      percentIncrease: percentIncrease.toFixed(2)
    });
  }, [baseCost, escalationRate, months]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Base Cost ($)</Label>
        <Input type="number" step="any" value={baseCost} onChange={(e) => setBaseCost(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Annual Rate %</Label>
          <Input type="number" step="any" value={escalationRate} onChange={(e) => setEscalationRate(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Months Out</Label>
          <Input type="number" value={months} onChange={(e) => setMonths(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Adjusted Cost</p>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${parseFloat(result.escalated).toLocaleString()}</p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            +${parseFloat(result.increase).toLocaleString()} ({result.percentIncrease}%)
          </p>
        </div>
      )}
    </div>
  );
}

// Rebar Estimator
function RebarEstimator({ darkMode }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [spacing, setSpacing] = useState('12');
  const [barSize, setBarSize] = useState('#4');
  const [result, setResult] = useState(null);

  const barWeights = {
    '#3': 0.376,
    '#4': 0.668,
    '#5': 1.043,
    '#6': 1.502,
    '#7': 2.044,
    '#8': 2.670
  };

  useEffect(() => {
    if (!length || !width) { setResult(null); return; }
    const l = parseFloat(length);
    const w = parseFloat(width);
    const sp = parseFloat(spacing) / 12; // convert to feet
    
    const barsLong = Math.ceil(w / sp) + 1;
    const barsWide = Math.ceil(l / sp) + 1;
    const totalBars = barsLong + barsWide;
    const totalLinFt = (barsLong * l) + (barsWide * w);
    const weight = totalLinFt * barWeights[barSize];
    
    setResult({
      barsLong,
      barsWide,
      totalBars,
      totalLinFt: totalLinFt.toFixed(0),
      weight: weight.toFixed(0)
    });
  }, [length, width, spacing, barSize]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Length (ft)</Label>
          <Input type="number" step="any" value={length} onChange={(e) => setLength(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Width (ft)</Label>
          <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Spacing (in O.C.)</Label>
          <Input 
            type="number" 
            step="any" 
            value={spacing} 
            onChange={(e) => setSpacing(e.target.value)}
            placeholder="e.g., 12"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} 
          />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Bar Size</Label>
          <Select value={barSize} onValueChange={setBarSize}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="#3">#3 (3/8")</SelectItem>
              <SelectItem value="#4">#4 (1/2")</SelectItem>
              <SelectItem value="#5">#5 (5/8")</SelectItem>
              <SelectItem value="#6">#6 (3/4")</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Total Bars</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.totalBars}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Linear Feet</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.totalLinFt}</p>
            </div>
          </div>
          <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Weight: ~{result.weight} lbs | {result.barsLong} long × {result.barsWide} wide
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// NEW CALCULATORS - ARCHITECTS/RESIDENTIAL
// ============================================

// Room Area Calculator
function RoomAreaCalculator({ darkMode }) {
  const [shape, setShape] = useState('rectangle');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [radius, setRadius] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let area = 0;
    if (shape === 'rectangle' && length && width) {
      area = parseFloat(length) * parseFloat(width);
    } else if (shape === 'circle' && radius) {
      area = Math.PI * Math.pow(parseFloat(radius), 2);
    } else if (shape === 'triangle' && length && width) {
      area = 0.5 * parseFloat(length) * parseFloat(width);
    }
    if (area > 0) {
      setResult({
        sqft: area.toFixed(2),
        sqm: (area * 0.0929).toFixed(2),
        sqyd: (area / 9).toFixed(2)
      });
    } else {
      setResult(null);
    }
  }, [shape, length, width, radius]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Shape</Label>
        <Select value={shape} onValueChange={setShape}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rectangle">Rectangle</SelectItem>
            <SelectItem value="circle">Circle</SelectItem>
            <SelectItem value="triangle">Triangle</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {shape !== 'circle' ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>{shape === 'triangle' ? 'Base (ft)' : 'Length (ft)'}</Label>
            <Input type="number" step="any" value={length} onChange={(e) => setLength(e.target.value)}
              className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          </div>
          <div>
            <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>{shape === 'triangle' ? 'Height (ft)' : 'Width (ft)'}</Label>
            <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
              className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          </div>
        </div>
      ) : (
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Radius (ft)</Label>
          <Input type="number" step="any" value={radius} onChange={(e) => setRadius(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      )}
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Sq Ft</p>
              <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.sqft}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Sq M</p>
              <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.sqm}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Sq Yd</p>
              <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.sqyd}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Window-to-Floor Ratio Calculator
function WindowFloorRatio({ darkMode }) {
  const [floorArea, setFloorArea] = useState('');
  const [windowArea, setWindowArea] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (floorArea && windowArea) {
      const ratio = (parseFloat(windowArea) / parseFloat(floorArea)) * 100;
      const minRequired = parseFloat(floorArea) * 0.08;
      setResult({
        ratio: ratio.toFixed(1),
        compliant: ratio >= 8,
        minWindow: minRequired.toFixed(1)
      });
    } else {
      setResult(null);
    }
  }, [floorArea, windowArea]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Floor Area (SF)</Label>
          <Input type="number" step="any" value={floorArea} onChange={(e) => setFloorArea(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Window Area (SF)</Label>
          <Input type="number" step="any" value={windowArea} onChange={(e) => setWindowArea(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Window/Floor Ratio</span>
            <Badge className={result.compliant ? 'bg-green-500' : 'bg-red-500'}>{result.compliant ? 'COMPLIANT' : 'BELOW MIN'}</Badge>
          </div>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.ratio}%</p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Min required (8%): {result.minWindow} SF glazing
          </p>
        </div>
      )}
    </div>
  );
}

// Ceiling Height Calculator
function CeilingHeightCalc({ darkMode }) {
  const [roomType, setRoomType] = useState('habitable');
  const [result, setResult] = useState(null);

  const requirements = {
    habitable: { min: 7, rec: '8-9', code: 'IRC R305.1' },
    bathroom: { min: 6.67, rec: '7-8', code: 'IRC R305.1' },
    hallway: { min: 7, rec: '8', code: 'IRC R305.1' },
    basement: { min: 6.67, rec: '7-8', code: 'IRC R305.1.1' },
    garage: { min: 7, rec: '8-10', code: 'Local Code' },
    attic: { min: 5, rec: '7+', code: 'IRC R305.1' },
  };

  useEffect(() => {
    setResult(requirements[roomType]);
  }, [roomType]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Room Type</Label>
        <Select value={roomType} onValueChange={setRoomType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="habitable">Living/Bedroom</SelectItem>
            <SelectItem value="bathroom">Bathroom</SelectItem>
            <SelectItem value="hallway">Hallway/Corridor</SelectItem>
            <SelectItem value="basement">Finished Basement</SelectItem>
            <SelectItem value="garage">Garage</SelectItem>
            <SelectItem value="attic">Attic (50% rule)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Minimum</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.min}' - 0"</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Recommended</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.rec}'</p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Reference: {result.code}
          </p>
        </div>
      )}
    </div>
  );
}

// ADA Clearance Checker
function ADAClearanceChecker({ darkMode }) {
  const [checkType, setCheckType] = useState('door');
  const [measurement, setMeasurement] = useState('');
  const [result, setResult] = useState(null);

  const adaRequirements = {
    door: { min: 32, rec: 36, unit: 'in', desc: 'Clear opening width' },
    hallway: { min: 36, rec: 42, unit: 'in', desc: 'Minimum clear width' },
    turnRadius: { min: 60, rec: 67, unit: 'in', desc: '180° wheelchair turn' },
    toilet: { min: 60, rec: 66, unit: 'in', desc: 'Side clearance from wall' },
    sink: { min: 30, rec: 36, unit: 'in', desc: 'Clear floor space width' },
    ramp: { min: 36, rec: 44, unit: 'in', desc: 'Minimum ramp width' },
  };

  useEffect(() => {
    if (measurement) {
      const req = adaRequirements[checkType];
      const val = parseFloat(measurement);
      setResult({
        ...req,
        value: val,
        meetsMin: val >= req.min,
        meetsRec: val >= req.rec
      });
    } else {
      setResult(null);
    }
  }, [checkType, measurement]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Check Type</Label>
        <Select value={checkType} onValueChange={(v) => { setCheckType(v); setMeasurement(''); }}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="door">Door Opening</SelectItem>
            <SelectItem value="hallway">Hallway Width</SelectItem>
            <SelectItem value="turnRadius">Wheelchair Turn</SelectItem>
            <SelectItem value="toilet">Toilet Clearance</SelectItem>
            <SelectItem value="sink">Sink Clearance</SelectItem>
            <SelectItem value="ramp">Ramp Width</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Your Measurement (inches)</Label>
        <Input type="number" step="any" value={measurement} onChange={(e) => setMeasurement(e.target.value)}
          placeholder={`Min: ${adaRequirements[checkType].min}"`}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{result.desc}</span>
            <Badge className={result.meetsMin ? (result.meetsRec ? 'bg-green-500' : 'bg-yellow-500') : 'bg-red-500'}>
              {result.meetsRec ? 'EXCEEDS' : (result.meetsMin ? 'MEETS MIN' : 'FAILS')}
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Yours</p>
              <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : ''}`}>{result.value}"</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Min</p>
              <p className={`text-lg font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{result.min}"</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Rec</p>
              <p className={`text-lg font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{result.rec}"</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// NEW CALCULATORS - INTERIOR DESIGNERS
// ============================================

// Wallpaper Calculator
function WallpaperCalculator({ darkMode }) {
  const [wallHeight, setWallHeight] = useState('');
  const [wallWidth, setWallWidth] = useState('');
  const [rollWidth, setRollWidth] = useState('20.5');
  const [rollLength, setRollLength] = useState('33');
  const [patternRepeat, setPatternRepeat] = useState('0');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (wallHeight && wallWidth) {
      const h = parseFloat(wallHeight);
      const w = parseFloat(wallWidth);
      const rw = parseFloat(rollWidth) / 12; // convert to feet
      const rl = parseFloat(rollLength);
      const pr = parseFloat(patternRepeat) / 12;
      
      const adjustedHeight = pr > 0 ? Math.ceil(h / pr) * pr : h;
      const stripsPerRoll = Math.floor(rl / adjustedHeight);
      const stripsNeeded = Math.ceil(w / rw);
      const rollsNeeded = Math.ceil(stripsNeeded / stripsPerRoll);
      const wasteRolls = Math.ceil(rollsNeeded * 0.1);
      
      setResult({
        strips: stripsNeeded,
        rolls: rollsNeeded,
        withWaste: rollsNeeded + wasteRolls,
        adjustedHeight: adjustedHeight.toFixed(1)
      });
    } else {
      setResult(null);
    }
  }, [wallHeight, wallWidth, rollWidth, rollLength, patternRepeat]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Height (ft)</Label>
          <Input type="number" step="any" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wall Width (ft)</Label>
          <Input type="number" step="any" value={wallWidth} onChange={(e) => setWallWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Roll Width (in)</Label>
          <Input type="number" step="any" value={rollWidth} onChange={(e) => setRollWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Pattern Repeat (in)</Label>
          <Input type="number" step="any" value={patternRepeat} onChange={(e) => setPatternRepeat(e.target.value)}
            placeholder="0 for none"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Strips Needed</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.strips}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Rolls (w/10% waste)</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.withWaste}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Carpet/Rug Size Guide
function RugSizeGuide({ darkMode }) {
  const [roomLength, setRoomLength] = useState('');
  const [roomWidth, setRoomWidth] = useState('');
  const [roomType, setRoomType] = useState('living');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (roomLength && roomWidth) {
      const l = parseFloat(roomLength);
      const w = parseFloat(roomWidth);
      
      const guides = {
        living: { subtract: 2, desc: 'Leave 12-18" from walls' },
        dining: { subtract: 4, desc: 'Extend 24" beyond table' },
        bedroom: { subtract: 2, desc: 'Extend 18-24" from bed' },
      };
      
      const guide = guides[roomType];
      const rugL = Math.max(l - guide.subtract, 4);
      const rugW = Math.max(w - guide.subtract, 3);
      
      // Standard rug sizes
      const standardSizes = ['4x6', '5x7', '5x8', '6x9', '7x10', '8x10', '8x11', '9x12', '10x13', '10x14', '12x15'];
      
      setResult({
        idealLength: rugL.toFixed(0),
        idealWidth: rugW.toFixed(0),
        desc: guide.desc,
        closest: standardSizes.find(s => {
          const [sw, sl] = s.split('x').map(Number);
          return sw >= rugW - 1 && sl >= rugL - 1;
        }) || '12x15+'
      });
    } else {
      setResult(null);
    }
  }, [roomLength, roomWidth, roomType]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Room Type</Label>
        <Select value={roomType} onValueChange={setRoomType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="living">Living Room</SelectItem>
            <SelectItem value="dining">Dining Room</SelectItem>
            <SelectItem value="bedroom">Bedroom</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Room Length (ft)</Label>
          <Input type="number" step="any" value={roomLength} onChange={(e) => setRoomLength(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Room Width (ft)</Label>
          <Input type="number" step="any" value={roomWidth} onChange={(e) => setRoomWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{result.desc}</p>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Ideal Size</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>
                {result.idealWidth}' × {result.idealLength}'
              </p>
            </div>
            <div className="text-right">
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Closest Standard</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.closest}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Furniture Scale Calculator
function FurnitureScaleCalc({ darkMode }) {
  const [roomSqFt, setRoomSqFt] = useState('');
  const [furnitureType, setFurnitureType] = useState('sofa');
  const [result, setResult] = useState(null);

  const guidelines = {
    sofa: { minRoom: 150, pctRange: [8, 12], typicalSizes: ['72"', '84"', '96"'] },
    sectional: { minRoom: 250, pctRange: [12, 18], typicalSizes: ['100"', '120"', '140"'] },
    diningTable: { minRoom: 120, pctRange: [10, 15], typicalSizes: ['48"', '60"', '72"', '84"'] },
    bed: { minRoom: 100, pctRange: [15, 25], typicalSizes: ['Queen 60x80', 'King 76x80', 'Cal King 72x84'] },
    desk: { minRoom: 80, pctRange: [8, 12], typicalSizes: ['48"', '60"', '72"'] },
  };

  useEffect(() => {
    if (roomSqFt) {
      const sqft = parseFloat(roomSqFt);
      const guide = guidelines[furnitureType];
      const minFurnitureSF = sqft * (guide.pctRange[0] / 100);
      const maxFurnitureSF = sqft * (guide.pctRange[1] / 100);
      
      setResult({
        minSF: minFurnitureSF.toFixed(0),
        maxSF: maxFurnitureSF.toFixed(0),
        roomOK: sqft >= guide.minRoom,
        minRoom: guide.minRoom,
        sizes: guide.typicalSizes
      });
    } else {
      setResult(null);
    }
  }, [roomSqFt, furnitureType]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Furniture Type</Label>
        <Select value={furnitureType} onValueChange={setFurnitureType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sofa">Sofa</SelectItem>
            <SelectItem value="sectional">Sectional</SelectItem>
            <SelectItem value="diningTable">Dining Table</SelectItem>
            <SelectItem value="bed">Bed</SelectItem>
            <SelectItem value="desk">Desk</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Room Size (SF)</Label>
        <Input type="number" step="any" value={roomSqFt} onChange={(e) => setRoomSqFt(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {!result.roomOK && (
            <p className="text-xs text-amber-500 mb-2">Room may be small. Min recommended: {result.minRoom} SF</p>
          )}
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Furniture footprint range:</p>
          <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>
            {result.minSF} - {result.maxSF} SF
          </p>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Typical sizes: {result.sizes.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

// Color Temperature Reference
function ColorTempReference({ darkMode }) {
  const [kelvin, setKelvin] = useState('3000');
  const [result, setResult] = useState(null);

  const tempRanges = [
    { min: 2200, max: 2700, name: 'Warm White', mood: 'Cozy, intimate', rooms: 'Bedrooms, living rooms', color: '#FFB46B' },
    { min: 2700, max: 3000, name: 'Soft White', mood: 'Relaxed, inviting', rooms: 'Living areas, dining', color: '#FFC98C' },
    { min: 3000, max: 3500, name: 'Neutral White', mood: 'Balanced, natural', rooms: 'Kitchens, bathrooms', color: '#FFE4C4' },
    { min: 3500, max: 4100, name: 'Cool White', mood: 'Bright, energizing', rooms: 'Offices, task areas', color: '#FFF5E6' },
    { min: 4100, max: 5000, name: 'Bright White', mood: 'Alert, productive', rooms: 'Garages, workshops', color: '#FFFFFF' },
    { min: 5000, max: 6500, name: 'Daylight', mood: 'Crisp, clinical', rooms: 'Art studios, medical', color: '#F0F8FF' },
  ];

  useEffect(() => {
    const k = parseInt(kelvin);
    const range = tempRanges.find(r => k >= r.min && k <= r.max);
    setResult(range || tempRanges[0]);
  }, [kelvin]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Color Temperature (Kelvin)</Label>
        <Input type="number" value={kelvin} onChange={(e) => setKelvin(e.target.value)}
          min="2200" max="6500" step="100"
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <input type="range" min="2200" max="6500" step="100" value={kelvin} onChange={(e) => setKelvin(e.target.value)}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
        style={{ background: 'linear-gradient(to right, #FFB46B, #FFC98C, #FFE4C4, #FFF5E6, #FFFFFF, #F0F8FF)' }} />
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: result.color }} />
            <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>{result.name}</p>
          </div>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            <strong>Mood:</strong> {result.mood}
          </p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            <strong>Best for:</strong> {result.rooms}
          </p>
        </div>
      )}
    </div>
  );
}

// Fabric Yardage Calculator
function FabricYardageCalc({ darkMode }) {
  const [projectType, setProjectType] = useState('drapes');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (width && height) {
      const w = parseFloat(width);
      const h = parseFloat(height);
      let yards = 0;
      let notes = '';
      
      switch(projectType) {
        case 'drapes':
          // 2.5x fullness, add 12" for hems
          yards = ((w * 2.5) / 54) * ((h + 12) / 36);
          notes = '2.5x fullness, 54" fabric';
          break;
        case 'cushion':
          // Box cushion: top + bottom + boxing
          yards = ((w * h * 2) + (w * 4 * 2) + (h * 4 * 2)) / (54 * 36);
          notes = 'Box cushion with 4" depth';
          break;
        case 'chair':
          // Standard chair seat + back
          yards = (w * h * 2.5) / (54 * 36);
          notes = 'Seat + back, 2.5x for pattern matching';
          break;
        case 'sofa':
          // Full sofa reupholstery
          yards = w / 12 * 2; // Rule of thumb: 2 yards per foot of sofa
          notes = 'Rule: ~2 yds per foot of sofa length';
          break;
        default:
          yards = (w * h) / (54 * 36);
      }
      
      setResult({
        yards: Math.ceil(yards * 10) / 10,
        withWaste: Math.ceil(yards * 1.15 * 10) / 10,
        notes
      });
    } else {
      setResult(null);
    }
  }, [projectType, width, height]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Project Type</Label>
        <Select value={projectType} onValueChange={setProjectType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="drapes">Drapes/Curtains</SelectItem>
            <SelectItem value="cushion">Box Cushion</SelectItem>
            <SelectItem value="chair">Chair Reupholstery</SelectItem>
            <SelectItem value="sofa">Sofa Reupholstery</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Width (in)</Label>
          <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Height/Length (in)</Label>
          <Input type="number" step="any" value={height} onChange={(e) => setHeight(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Yards Needed</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.yards}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>With 15% Waste</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.withWaste}</p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{result.notes}</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// NEW CALCULATORS - CONTRACTORS
// ============================================

// Concrete Footing Calculator
function FootingCalculator({ darkMode }) {
  const [loadType, setLoadType] = useState('residential');
  const [stories, setStories] = useState('1');
  const [soilType, setSoilType] = useState('1500');
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadPerFoot = { residential: 1200, commercial: 2000 }; // lbs per linear foot
    const storiesNum = parseInt(stories);
    const soilBearing = parseInt(soilType);
    
    const totalLoad = loadPerFoot[loadType] * storiesNum;
    const reqWidth = (totalLoad / soilBearing) * 12; // convert to inches
    const minWidth = Math.max(reqWidth, loadType === 'residential' ? 16 : 24);
    const depth = loadType === 'residential' ? 8 : 12;
    
    setResult({
      width: Math.ceil(minWidth),
      depth,
      loadPerFt: totalLoad,
      concrete: ((minWidth / 12) * (depth / 12)).toFixed(2)
    });
  }, [loadType, stories, soilType]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Building Type</Label>
          <Select value={loadType} onValueChange={setLoadType}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Stories</Label>
          <Select value={stories} onValueChange={setStories}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Story</SelectItem>
              <SelectItem value="2">2 Stories</SelectItem>
              <SelectItem value="3">3 Stories</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Soil Bearing (PSF)</Label>
        <Select value={soilType} onValueChange={setSoilType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1000">Soft Clay (1000)</SelectItem>
            <SelectItem value="1500">Medium Clay (1500)</SelectItem>
            <SelectItem value="2000">Sandy Clay (2000)</SelectItem>
            <SelectItem value="3000">Sand/Gravel (3000)</SelectItem>
            <SelectItem value="4000">Compacted (4000)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Width</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.width}"</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Depth</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.depth}"</p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            CF per linear ft: {result.concrete} | Load: {result.loadPerFt} lbs/ft
          </p>
        </div>
      )}
    </div>
  );
}

// Beam Span Calculator
function BeamSpanCalc({ darkMode }) {
  const [loadType, setLoadType] = useState('floor');
  const [span, setSpan] = useState('');
  const [result, setResult] = useState(null);

  // Simplified span tables for common applications
  const spanTables = {
    floor: {
      name: 'Floor Joist',
      spans: [
        { size: '2x6', max: 9 },
        { size: '2x8', max: 12 },
        { size: '2x10', max: 15 },
        { size: '2x12', max: 18 },
        { size: 'LVL 1¾x9¼', max: 14 },
        { size: 'LVL 1¾x11⅞', max: 18 },
      ]
    },
    header: {
      name: 'Header/Beam',
      spans: [
        { size: '(2) 2x6', max: 4 },
        { size: '(2) 2x8', max: 5 },
        { size: '(2) 2x10', max: 6 },
        { size: '(2) 2x12', max: 8 },
        { size: 'LVL 3½x9¼', max: 8 },
        { size: 'LVL 3½x11⅞', max: 10 },
      ]
    },
    deck: {
      name: 'Deck Joist',
      spans: [
        { size: '2x6', max: 8 },
        { size: '2x8', max: 11 },
        { size: '2x10', max: 14 },
        { size: '2x12', max: 17 },
      ]
    }
  };

  useEffect(() => {
    if (span) {
      const s = parseFloat(span);
      const table = spanTables[loadType];
      const options = table.spans.filter(opt => opt.max >= s);
      const minOption = options[0] || table.spans[table.spans.length - 1];
      
      setResult({
        type: table.name,
        recommended: minOption.size,
        maxSpan: minOption.max,
        needsEngineer: options.length === 0,
        allOptions: options.slice(0, 3)
      });
    } else {
      setResult(null);
    }
  }, [loadType, span]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Application</Label>
        <Select value={loadType} onValueChange={setLoadType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="floor">Floor Joist (40 PSF)</SelectItem>
            <SelectItem value="header">Header/Beam</SelectItem>
            <SelectItem value="deck">Deck Joist</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Span Required (ft)</Label>
        <Input type="number" step="0.5" value={span} onChange={(e) => setSpan(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {result.needsEngineer ? (
            <p className="text-amber-500 text-sm">Span exceeds tables - consult engineer</p>
          ) : (
            <>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Minimum Size for {result.type}</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.recommended}</p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Max span: {result.maxSpan}ft</p>
              {result.allOptions.length > 1 && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                  Also works: {result.allOptions.slice(1).map(o => o.size).join(', ')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Shingle Calculator
function ShingleCalculator({ darkMode }) {
  const [roofSqFt, setRoofSqFt] = useState('');
  const [pitch, setPitch] = useState('6');
  const [result, setResult] = useState(null);

  const pitchMultipliers = {
    '3': 1.031, '4': 1.054, '5': 1.083, '6': 1.118,
    '7': 1.158, '8': 1.202, '9': 1.250, '10': 1.302, '12': 1.414
  };

  useEffect(() => {
    if (roofSqFt) {
      const flatArea = parseFloat(roofSqFt);
      const mult = pitchMultipliers[pitch] || 1.118;
      const actualArea = flatArea * mult;
      const squares = actualArea / 100;
      const bundles = Math.ceil(squares * 3); // 3 bundles per square
      const bundlesWithWaste = Math.ceil(bundles * 1.1); // 10% waste
      const ridgeCap = Math.ceil(squares * 3); // linear feet estimate
      
      setResult({
        squares: squares.toFixed(1),
        bundles: bundlesWithWaste,
        area: Math.ceil(actualArea),
        ridgeCap
      });
    } else {
      setResult(null);
    }
  }, [roofSqFt, pitch]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Roof Area (SF flat)</Label>
          <Input type="number" step="any" value={roofSqFt} onChange={(e) => setRoofSqFt(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Pitch (X:12)</Label>
          <Select value={pitch} onValueChange={setPitch}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(pitchMultipliers).map(p => (
                <SelectItem key={p} value={p}>{p}:12</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Squares</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.squares}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Bundles (+10%)</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.bundles}</p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Actual area: {result.area} SF | Ridge cap: ~{result.ridgeCap} LF
          </p>
        </div>
      )}
    </div>
  );
}

// Gutter Sizing Calculator
function GutterSizingCalc({ darkMode }) {
  const [roofArea, setRoofArea] = useState('');
  const [rainfall, setRainfall] = useState('4');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (roofArea) {
      const area = parseFloat(roofArea);
      const rain = parseFloat(rainfall);
      
      // Calculate drainage factor (area × rainfall intensity)
      const drainageFactor = area * rain;
      
      // Gutter sizing based on drainage factor
      let gutterSize, downspoutSize, downspoutCount;
      if (drainageFactor <= 2800) {
        gutterSize = '5"';
        downspoutSize = '2x3"';
        downspoutCount = Math.ceil(area / 1200);
      } else if (drainageFactor <= 5200) {
        gutterSize = '6"';
        downspoutSize = '3x4"';
        downspoutCount = Math.ceil(area / 1600);
      } else {
        gutterSize = '6" Commercial';
        downspoutSize = '4x5"';
        downspoutCount = Math.ceil(area / 2000);
      }
      
      setResult({
        gutter: gutterSize,
        downspout: downspoutSize,
        count: Math.max(2, downspoutCount),
        drainageFactor: Math.round(drainageFactor)
      });
    } else {
      setResult(null);
    }
  }, [roofArea, rainfall]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Roof Area (SF)</Label>
          <Input type="number" step="any" value={roofArea} onChange={(e) => setRoofArea(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Rainfall (in/hr)</Label>
          <Select value={rainfall} onValueChange={setRainfall}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">Light (2")</SelectItem>
              <SelectItem value="4">Moderate (4")</SelectItem>
              <SelectItem value="6">Heavy (6")</SelectItem>
              <SelectItem value="8">Extreme (8")</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Gutter Size</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.gutter}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Downspouts</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.count}× {result.downspout}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Excavation Volume Calculator
function ExcavationCalc({ darkMode }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState('');
  const [swell, setSwell] = useState('25');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (length && width && depth) {
      const l = parseFloat(length);
      const w = parseFloat(width);
      const d = parseFloat(depth);
      const swellFactor = 1 + (parseFloat(swell) / 100);
      
      const cubicFt = l * w * d;
      const cubicYd = cubicFt / 27;
      const cubicYdWithSwell = cubicYd * swellFactor;
      const truckLoads = Math.ceil(cubicYdWithSwell / 10); // 10 CY per truck
      
      setResult({
        cubicFt: Math.round(cubicFt),
        cubicYd: cubicYd.toFixed(1),
        withSwell: cubicYdWithSwell.toFixed(1),
        truckLoads
      });
    } else {
      setResult(null);
    }
  }, [length, width, depth, swell]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>L (ft)</Label>
          <Input type="number" step="any" value={length} onChange={(e) => setLength(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>W (ft)</Label>
          <Input type="number" step="any" value={width} onChange={(e) => setWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>D (ft)</Label>
          <Input type="number" step="any" value={depth} onChange={(e) => setDepth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Swell Factor %</Label>
        <Select value={swell} onValueChange={setSwell}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="20">Sand (20%)</SelectItem>
            <SelectItem value="25">Earth/Loam (25%)</SelectItem>
            <SelectItem value="30">Clay (30%)</SelectItem>
            <SelectItem value="50">Rock (50%)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Bank CY</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.cubicYd}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Loose CY</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.withSwell}</p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            {result.cubicFt} CF | ~{result.truckLoads} truck loads (10 CY ea)
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// NEW CALCULATORS - ENGINEERS
// ============================================

// Snow Load Calculator
function SnowLoadCalc({ darkMode }) {
  const [groundSnow, setGroundSnow] = useState('');
  const [roofType, setRoofType] = useState('heated');
  const [exposure, setExposure] = useState('partial');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (groundSnow) {
      const pg = parseFloat(groundSnow);
      
      // Exposure factors
      const Ce = { full: 0.8, partial: 0.9, sheltered: 1.0 }[exposure];
      // Thermal factor
      const Ct = { heated: 1.0, unheated: 1.1, freezer: 1.3 }[roofType];
      // Importance factor (assume residential = 1.0)
      const Is = 1.0;
      
      // Flat roof snow load: pf = 0.7 × Ce × Ct × Is × pg
      const pf = 0.7 * Ce * Ct * Is * pg;
      // Minimum for low-slope
      const minLoad = pg * Is;
      const flatRoof = Math.max(pf, 20 * Is);
      
      setResult({
        flatRoof: flatRoof.toFixed(1),
        groundSnow: pg,
        Ce: Ce,
        Ct: Ct,
        minDrift: (pg * 1.5).toFixed(1)
      });
    } else {
      setResult(null);
    }
  }, [groundSnow, roofType, exposure]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Ground Snow Load (PSF)</Label>
        <Input type="number" step="any" value={groundSnow} onChange={(e) => setGroundSnow(e.target.value)}
          placeholder="Check local code"
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Roof Type</Label>
          <Select value={roofType} onValueChange={setRoofType}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="heated">Heated Below</SelectItem>
              <SelectItem value="unheated">Unheated</SelectItem>
              <SelectItem value="freezer">Cold Room Below</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Exposure</Label>
          <Select value={exposure} onValueChange={setExposure}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Fully Exposed</SelectItem>
              <SelectItem value="partial">Partially Exposed</SelectItem>
              <SelectItem value="sheltered">Sheltered</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Flat/Low-Slope Roof Load</p>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.flatRoof} PSF</p>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Ce={result.Ce} | Ct={result.Ct} | Check drift: ~{result.minDrift} PSF
          </p>
        </div>
      )}
    </div>
  );
}

// Wind Load Reference
function WindLoadReference({ darkMode }) {
  const [windSpeed, setWindSpeed] = useState('115');
  const [exposure, setExposure] = useState('B');
  const [height, setHeight] = useState('30');
  const [result, setResult] = useState(null);

  useEffect(() => {
    const V = parseFloat(windSpeed);
    const h = parseFloat(height);
    
    // Kz values for different exposures and heights
    const KzTable = {
      B: { 15: 0.57, 20: 0.62, 25: 0.66, 30: 0.70, 40: 0.76, 50: 0.81 },
      C: { 15: 0.85, 20: 0.90, 25: 0.94, 30: 0.98, 40: 1.04, 50: 1.09 },
      D: { 15: 1.03, 20: 1.08, 25: 1.12, 30: 1.16, 40: 1.22, 50: 1.27 },
    };
    
    // Find closest height
    const heights = [15, 20, 25, 30, 40, 50];
    const closestH = heights.reduce((prev, curr) => 
      Math.abs(curr - h) < Math.abs(prev - h) ? curr : prev
    );
    
    const Kz = KzTable[exposure][closestH];
    const Kzt = 1.0; // Topographic factor (flat terrain)
    const Kd = 0.85; // Directionality factor
    const qz = 0.00256 * Kz * Kzt * Kd * V * V;
    
    setResult({
      qz: qz.toFixed(2),
      Kz: Kz,
      velocity: V,
      pressureRange: `${(qz * 0.8).toFixed(1)} - ${(qz * 1.3).toFixed(1)}`
    });
  }, [windSpeed, exposure, height]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Wind Speed (mph)</Label>
          <Select value={windSpeed} onValueChange={setWindSpeed}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="95">95 mph</SelectItem>
              <SelectItem value="105">105 mph</SelectItem>
              <SelectItem value="115">115 mph</SelectItem>
              <SelectItem value="120">120 mph</SelectItem>
              <SelectItem value="130">130 mph</SelectItem>
              <SelectItem value="140">140 mph</SelectItem>
              <SelectItem value="150">150 mph (Hurricane)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Exposure</Label>
          <Select value={exposure} onValueChange={setExposure}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="B">B - Urban/Suburban</SelectItem>
              <SelectItem value="C">C - Open Terrain</SelectItem>
              <SelectItem value="D">D - Coastal/Flat</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Mean Roof Height (ft)</Label>
        <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Velocity Pressure (qz)</p>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.qz} PSF</p>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Kz={result.Kz} | Wall pressure range: {result.pressureRange} PSF
          </p>
        </div>
      )}
    </div>
  );
}

// Seismic Zone Reference
function SeismicZoneRef({ darkMode }) {
  const [siteClass, setSiteClass] = useState('D');
  const [sds, setSds] = useState('');
  const [result, setResult] = useState(null);

  const siteDescriptions = {
    A: 'Hard rock',
    B: 'Rock',
    C: 'Dense soil/soft rock',
    D: 'Stiff soil (default)',
    E: 'Soft clay',
  };

  useEffect(() => {
    if (sds) {
      const SDS = parseFloat(sds);
      let category, requirements;
      
      if (SDS < 0.167) {
        category = 'A';
        requirements = 'Minimal seismic detailing required';
      } else if (SDS < 0.33) {
        category = 'B';
        requirements = 'Basic seismic detailing';
      } else if (SDS < 0.50) {
        category = 'C';
        requirements = 'Intermediate seismic detailing';
      } else {
        category = 'D/E/F';
        requirements = 'Full seismic detailing required';
      }
      
      setResult({
        category,
        requirements,
        siteClass: siteDescriptions[siteClass],
        baseShear: (SDS * 0.15).toFixed(3) // Simplified residential estimate
      });
    } else {
      setResult(null);
    }
  }, [sds, siteClass]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Site Class</Label>
        <Select value={siteClass} onValueChange={setSiteClass}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(siteDescriptions).map(([key, desc]) => (
              <SelectItem key={key} value={key}>{key} - {desc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>SDS (from USGS or local code)</Label>
        <Input type="number" step="0.01" value={sds} onChange={(e) => setSds(e.target.value)}
          placeholder="e.g., 0.50"
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Seismic Design Category</span>
            <Badge className={result.category.includes('D') ? 'bg-red-500' : 'bg-green-500'}>{result.category}</Badge>
          </div>
          <p className={`text-sm ${darkMode ? 'text-white' : ''}`}>{result.requirements}</p>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Est. base shear coef: {result.baseShear}W (residential)
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// NEW CALCULATORS - GENERAL/CROSS-FUNCTIONAL
// ============================================

// Material Waste Calculator
function MaterialWasteCalc({ darkMode }) {
  const [material, setMaterial] = useState('drywall');
  const [quantity, setQuantity] = useState('');
  const [result, setResult] = useState(null);

  const wasteFactors = {
    drywall: { pct: 10, name: 'Drywall', unit: 'sheets' },
    lumber: { pct: 10, name: 'Lumber', unit: 'BF' },
    plywood: { pct: 8, name: 'Plywood/OSB', unit: 'sheets' },
    roofing: { pct: 10, name: 'Shingles', unit: 'bundles' },
    tile: { pct: 15, name: 'Tile', unit: 'SF' },
    flooring: { pct: 10, name: 'Flooring', unit: 'SF' },
    siding: { pct: 10, name: 'Siding', unit: 'SF' },
    insulation: { pct: 5, name: 'Insulation', unit: 'SF' },
    concrete: { pct: 5, name: 'Concrete', unit: 'CY' },
    paint: { pct: 10, name: 'Paint', unit: 'gallons' },
  };

  useEffect(() => {
    if (quantity) {
      const qty = parseFloat(quantity);
      const factor = wasteFactors[material];
      const wasteQty = qty * (factor.pct / 100);
      const totalQty = qty + wasteQty;
      
      setResult({
        original: qty,
        waste: wasteQty.toFixed(1),
        total: Math.ceil(totalQty),
        pct: factor.pct,
        unit: factor.unit
      });
    } else {
      setResult(null);
    }
  }, [material, quantity]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Material Type</Label>
        <Select value={material} onValueChange={setMaterial}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(wasteFactors).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.name} ({val.pct}%)</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Net Quantity Needed</Label>
        <Input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)}
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Waste (+{result.pct}%)</p>
              <p className={`text-lg font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>+{result.waste}</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Order Qty</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.total} {result.unit}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Lead Time Reference
function LeadTimeReference({ darkMode }) {
  const [category, setCategory] = useState('windows');
  
  const leadTimes = {
    windows: { name: 'Windows (standard)', weeks: '4-6', rush: '2-3', custom: '8-12' },
    doors: { name: 'Exterior Doors', weeks: '3-5', rush: '1-2', custom: '6-10' },
    cabinets: { name: 'Cabinets (semi-custom)', weeks: '6-10', rush: '3-4', custom: '12-16' },
    countertops: { name: 'Countertops', weeks: '2-4', rush: '1-2', custom: '4-8' },
    appliances: { name: 'Appliances', weeks: '2-4', rush: '1', custom: '8-16' },
    hvac: { name: 'HVAC Equipment', weeks: '1-3', rush: '1', custom: '4-8' },
    roofing: { name: 'Roofing Materials', weeks: '1-2', rush: '1', custom: '3-4' },
    lumber: { name: 'Framing Lumber', weeks: '1-2', rush: '1', custom: 'N/A' },
    tile: { name: 'Tile/Stone', weeks: '2-4', rush: '1-2', custom: '6-12' },
    fixtures: { name: 'Plumbing Fixtures', weeks: '2-4', rush: '1-2', custom: '8-12' },
  };

  const item = leadTimes[category];

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Material/Product</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(leadTimes).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Standard</p>
            <p className={`text-lg font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{item.weeks}</p>
            <p className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>weeks</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Rush</p>
            <p className={`text-lg font-mono ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{item.rush}</p>
            <p className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>weeks</p>
          </div>
          <div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Custom</p>
            <p className={`text-lg font-mono ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{item.custom}</p>
            <p className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>weeks</p>
          </div>
        </div>
        <p className={`text-[10px] mt-2 text-center ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
          Lead times vary by supplier and region
        </p>
      </div>
    </div>
  );
}

// Permit Fee Estimator
function PermitFeeEstimator({ darkMode }) {
  const [projectValue, setProjectValue] = useState('');
  const [projectType, setProjectType] = useState('remodel');
  const [result, setResult] = useState(null);

  const feeStructures = {
    newConstruction: { base: 500, perThousand: 8, plan: 150, name: 'New Construction' },
    addition: { base: 350, perThousand: 7, plan: 100, name: 'Addition' },
    remodel: { base: 200, perThousand: 5, plan: 75, name: 'Remodel' },
    mechanical: { base: 100, perThousand: 0, plan: 0, name: 'Mechanical Only' },
    electrical: { base: 85, perThousand: 0, plan: 0, name: 'Electrical Only' },
    plumbing: { base: 85, perThousand: 0, plan: 0, name: 'Plumbing Only' },
  };

  useEffect(() => {
    if (projectValue) {
      const value = parseFloat(projectValue);
      const fee = feeStructures[projectType];
      const valueFee = (value / 1000) * fee.perThousand;
      const total = fee.base + valueFee + fee.plan;
      
      setResult({
        base: fee.base,
        valueFee: valueFee.toFixed(0),
        planReview: fee.plan,
        total: Math.ceil(total),
        lowEstimate: Math.ceil(total * 0.7),
        highEstimate: Math.ceil(total * 1.5)
      });
    } else {
      setResult(null);
    }
  }, [projectValue, projectType]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Project Type</Label>
        <Select value={projectType} onValueChange={setProjectType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(feeStructures).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Project Value ($)</Label>
        <Input type="number" step="1000" value={projectValue} onChange={(e) => setProjectValue(e.target.value)}
          placeholder="Construction cost"
          className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
      </div>
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Estimated Permit Fee</p>
          <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>${result.total}</p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Range: ${result.lowEstimate} - ${result.highEstimate}
          </p>
          <p className={`text-[10px] mt-2 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
            Base: ${result.base} | Value: ${result.valueFee} | Plan: ${result.planReview}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// DECK MATERIALS CALCULATOR
// ============================================

function DeckMaterialsCalc({ darkMode }) {
  const [deckLength, setDeckLength] = useState('');
  const [deckWidth, setDeckWidth] = useState('');
  const [boardWidth, setBoardWidth] = useState('5.5'); // Standard deck board width
  const [boardLength, setBoardLength] = useState('16'); // 16ft boards
  const [railingLength, setRailingLength] = useState('');
  const [stairCount, setStairCount] = useState('0');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (deckLength && deckWidth) {
      const l = parseFloat(deckLength);
      const w = parseFloat(deckWidth);
      const bw = parseFloat(boardWidth) / 12; // Convert to feet
      const bl = parseFloat(boardLength);
      const rail = parseFloat(railingLength) || 0;
      const stairs = parseInt(stairCount) || 0;
      
      // Deck area and boards
      const deckSqFt = l * w;
      const boardsPerRow = Math.ceil(w / bw);
      const rowsNeeded = Math.ceil(l / bl);
      const totalDeckBoards = Math.ceil(boardsPerRow * rowsNeeded * 1.1); // 10% waste
      
      // Joists (16" OC)
      const joistCount = Math.ceil(l / 1.33) + 1;
      const joistLength = w;
      
      // Rim joists (2 per side)
      const rimJoistLF = (l * 2) + (w * 2);
      
      // Ledger board
      const ledgerLF = w;
      
      // Posts (one every 6ft of beam span, plus corners)
      const postCount = Math.ceil(w / 6) + 1;
      
      // Beams (2x doubled-up)
      const beamLF = w * 2;
      
      // Railing materials (if provided)
      let railPosts = 0;
      let railTopRailLF = 0;
      let railBottomRailLF = 0;
      let balusters = 0;
      let postCaps = 0;
      
      if (rail > 0) {
        railPosts = Math.ceil(rail / 6) + 1; // Post every 6ft
        railTopRailLF = rail;
        railBottomRailLF = rail;
        balusters = Math.ceil(rail / 0.33); // Baluster every 4"
        postCaps = railPosts;
      }
      
      // Stair materials
      let stringers = 0;
      let treads = 0;
      let risers = 0;
      
      if (stairs > 0) {
        stringers = 3; // Standard 3 stringers
        treads = stairs * 2; // 2 boards per tread
        risers = stairs;
      }
      
      // Hardware estimates
      const deckScrews = Math.ceil(deckSqFt * 25); // ~25 screws per SF
      const joisHangers = joistCount;
      const lagBolts = postCount * 2;
      const carriage3_8 = postCount * 4;
      
      setResult({
        // Decking
        deckSqFt: Math.round(deckSqFt),
        deckBoards: totalDeckBoards,
        boardLengthUsed: bl,
        // Framing
        joists: { count: joistCount, length: Math.ceil(joistLength) },
        rimJoistLF: Math.ceil(rimJoistLF),
        ledgerLF: Math.ceil(ledgerLF),
        posts: postCount,
        beamLF: Math.ceil(beamLF),
        // Railing
        railPosts,
        railTopRailLF: Math.ceil(railTopRailLF),
        balusters,
        postCaps,
        // Stairs
        stringers,
        treads,
        risers,
        // Hardware
        deckScrews,
        joisHangers,
        lagBolts,
        carriageBolts: carriage3_8
      });
    } else {
      setResult(null);
    }
  }, [deckLength, deckWidth, boardWidth, boardLength, railingLength, stairCount]);

  return (
    <div className="space-y-2">
      <p className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
        Deck Dimensions
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Length (ft)</Label>
          <Input type="number" step="any" value={deckLength} onChange={(e) => setDeckLength(e.target.value)}
            placeholder="20"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Width (ft)</Label>
          <Input type="number" step="any" value={deckWidth} onChange={(e) => setDeckWidth(e.target.value)}
            placeholder="12"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Board Width</Label>
          <Select value={boardWidth} onValueChange={setBoardWidth}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5.5">5.5" (Standard)</SelectItem>
              <SelectItem value="3.5">3.5" (Narrow)</SelectItem>
              <SelectItem value="7.25">7.25" (Wide)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Board Length</Label>
          <Select value={boardLength} onValueChange={setBoardLength}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="8">8 ft</SelectItem>
              <SelectItem value="10">10 ft</SelectItem>
              <SelectItem value="12">12 ft</SelectItem>
              <SelectItem value="16">16 ft</SelectItem>
              <SelectItem value="20">20 ft</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <p className={`text-[10px] mt-2 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
        Optional: Railing & Stairs
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Railing (LF)</Label>
          <Input type="number" step="any" value={railingLength} onChange={(e) => setRailingLength(e.target.value)}
            placeholder="0"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Stair Treads</Label>
          <Input type="number" value={stairCount} onChange={(e) => setStairCount(e.target.value)}
            placeholder="0"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
      </div>
      
      {result && (
        <div className={`p-2 rounded-lg space-y-2 ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="text-center border-b pb-2 mb-2 border-gray-600/20">
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Deck Area</p>
            <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.deckSqFt} SF</p>
          </div>
          
          <div>
            <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>DECKING</p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              {result.deckBoards} boards @ {result.boardLengthUsed}ft
            </p>
          </div>
          
          <div>
            <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>FRAMING</p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Joists: {result.joists.count} @ {result.joists.length}ft (2x10)
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Rim/Band: {result.rimJoistLF} LF (2x10)
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Ledger: {result.ledgerLF} LF (2x10)
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Beams: {result.beamLF} LF (2x10 doubled)
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Posts: {result.posts} (6x6 or 4x4)
            </p>
          </div>
          
          {result.railPosts > 0 && (
            <div>
              <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>RAILING</p>
              <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
                Posts: {result.railPosts} | Top Rail: {result.railTopRailLF} LF
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
                Balusters: {result.balusters} | Post Caps: {result.postCaps}
              </p>
            </div>
          )}
          
          {result.stringers > 0 && (
            <div>
              <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>STAIRS</p>
              <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
                Stringers: {result.stringers} | Treads: {result.treads} boards
              </p>
            </div>
          )}
          
          <div>
            <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>HARDWARE</p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Deck Screws: ~{result.deckScrews} | Joist Hangers: {result.joisHangers}
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>
              Lag Bolts: {result.lagBolts} | Carriage Bolts: {result.carriageBolts}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// NAIL/FASTENER CALCULATOR
// ============================================

function FastenerCalc({ darkMode }) {
  const [application, setApplication] = useState('framing');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('sqft');
  const [result, setResult] = useState(null);

  // Fastener requirements by application (per unit)
  const fastenerSpecs = {
    framing: {
      name: 'Framing (2x Lumber)',
      perSqFt: 1.5,
      perLF: 0.5,
      perPiece: 6,
      nailType: '16d Common (3.5")',
      nailsPerLb: 50,
      screwAlt: '#9 x 3" Structural'
    },
    sheathing: {
      name: 'Wall/Roof Sheathing',
      perSqFt: 0.75,
      perLF: null,
      perPiece: 32,
      nailType: '8d Common (2.5")',
      nailsPerLb: 100,
      screwAlt: '#8 x 2" Wood'
    },
    subfloor: {
      name: 'Subfloor (3/4")',
      perSqFt: 1.2,
      perLF: null,
      perPiece: 40,
      nailType: '8d Ring Shank',
      nailsPerLb: 95,
      screwAlt: '#8 x 2.5" Subfloor'
    },
    decking: {
      name: 'Deck Boards',
      perSqFt: 2.5,
      perLF: 4,
      perPiece: null,
      nailType: '16d HDG Ring Shank',
      nailsPerLb: 45,
      screwAlt: '#10 x 3" Deck Screw'
    },
    siding: {
      name: 'Wood Siding',
      perSqFt: 1.0,
      perLF: null,
      perPiece: null,
      nailType: '8d HDG Siding',
      nailsPerLb: 90,
      screwAlt: '#8 x 2" SS Trim'
    },
    trim: {
      name: 'Trim/Finish',
      perSqFt: null,
      perLF: 3,
      perPiece: 8,
      nailType: '6d/8d Finish',
      nailsPerLb: 150,
      screwAlt: '#6 x 1.5" Trim'
    },
    drywall: {
      name: 'Drywall',
      perSqFt: 0.9,
      perLF: null,
      perPiece: 32,
      nailType: '1.25" Drywall Nail',
      nailsPerLb: 280,
      screwAlt: '#6 x 1.25" Drywall'
    },
    roofing: {
      name: 'Asphalt Shingles',
      perSqFt: 0.8,
      perLF: null,
      perPiece: null,
      nailType: '1.25" Roofing',
      nailsPerLb: 140,
      screwAlt: 'N/A - Use Nails'
    },
    hardwood: {
      name: 'Hardwood Flooring',
      perSqFt: 3.5,
      perLF: null,
      perPiece: null,
      nailType: '2" Flooring Cleats',
      nailsPerLb: 200,
      screwAlt: 'N/A - Use Cleats'
    },
    joist_hanger: {
      name: 'Joist Hangers',
      perSqFt: null,
      perLF: null,
      perPiece: 10,
      nailType: '1.5" Joist Hanger',
      nailsPerLb: 175,
      screwAlt: '#9 x 1.5" Structural'
    }
  };

  useEffect(() => {
    if (quantity && application) {
      const qty = parseFloat(quantity);
      const spec = fastenerSpecs[application];
      let nailCount = 0;
      
      if (unit === 'sqft' && spec.perSqFt) {
        nailCount = qty * spec.perSqFt;
      } else if (unit === 'lf' && spec.perLF) {
        nailCount = qty * spec.perLF;
      } else if (unit === 'pieces' && spec.perPiece) {
        nailCount = qty * spec.perPiece;
      }
      
      // Add 10% waste factor
      const nailsWithWaste = Math.ceil(nailCount * 1.1);
      const poundsNeeded = Math.ceil(nailsWithWaste / spec.nailsPerLb);
      
      // Estimate boxes (typically 1lb, 5lb, 25lb, 50lb)
      let boxSuggestion = '';
      if (poundsNeeded <= 1) boxSuggestion = '1 lb box';
      else if (poundsNeeded <= 5) boxSuggestion = '5 lb box';
      else if (poundsNeeded <= 25) boxSuggestion = '1 x 25 lb box';
      else if (poundsNeeded <= 50) boxSuggestion = '1 x 50 lb box';
      else boxSuggestion = `${Math.ceil(poundsNeeded / 50)} x 50 lb boxes`;
      
      setResult({
        nailCount: nailsWithWaste,
        pounds: poundsNeeded,
        nailType: spec.nailType,
        nailsPerLb: spec.nailsPerLb,
        screwAlt: spec.screwAlt,
        boxSuggestion,
        appName: spec.name
      });
    } else {
      setResult(null);
    }
  }, [quantity, application, unit]);

  const spec = fastenerSpecs[application];
  const availableUnits = [];
  if (spec?.perSqFt) availableUnits.push({ value: 'sqft', label: 'Square Feet' });
  if (spec?.perLF) availableUnits.push({ value: 'lf', label: 'Linear Feet' });
  if (spec?.perPiece) availableUnits.push({ value: 'pieces', label: 'Pieces/Sheets' });

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Application</Label>
        <Select value={application} onValueChange={(v) => { setApplication(v); setUnit('sqft'); }}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="framing">Framing (2x Lumber)</SelectItem>
            <SelectItem value="sheathing">Wall/Roof Sheathing</SelectItem>
            <SelectItem value="subfloor">Subfloor</SelectItem>
            <SelectItem value="decking">Deck Boards</SelectItem>
            <SelectItem value="siding">Wood Siding</SelectItem>
            <SelectItem value="trim">Trim/Finish</SelectItem>
            <SelectItem value="drywall">Drywall</SelectItem>
            <SelectItem value="roofing">Asphalt Shingles</SelectItem>
            <SelectItem value="hardwood">Hardwood Flooring</SelectItem>
            <SelectItem value="joist_hanger">Joist Hangers</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Quantity</Label>
          <Input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter amount"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Unit</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableUnits.map(u => (
                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {result && (
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          <div className="text-center border-b pb-2 mb-2 border-gray-600/20">
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Fasteners Needed</p>
            <p className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>
              ~{result.nailCount.toLocaleString()}
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              ({result.pounds} lbs)
            </p>
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Nail Type:</span>
              <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>{result.nailType}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Nails/lb:</span>
              <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>~{result.nailsPerLb}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Buy:</span>
              <span className={`font-medium text-green-600`}>{result.boxSuggestion}</span>
            </div>
            {result.screwAlt !== 'N/A - Use Nails' && result.screwAlt !== 'N/A - Use Cleats' && (
              <div className="flex justify-between text-xs mt-1 pt-1 border-t border-gray-600/20">
                <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Screw Alt:</span>
                <span className={`font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{result.screwAlt}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// STAIR CALCULATOR
// ============================================

function StairCalculator({ darkMode }) {
  const [totalRise, setTotalRise] = useState(''); // Total height in inches
  const [stairWidth, setStairWidth] = useState('36'); // Width in inches
  const [codeType, setCodeType] = useState('residential');
  const [result, setResult] = useState(null);

  // Building code requirements
  const codeRequirements = {
    residential: {
      name: 'Residential (IRC)',
      maxRise: 7.75,
      minRun: 10,
      minHeadroom: 80,
      minWidth: 36,
      maxNosing: 1.25,
      minNosing: 0.75
    },
    commercial: {
      name: 'Commercial (IBC)',
      maxRise: 7,
      minRun: 11,
      minHeadroom: 80,
      minWidth: 44,
      maxNosing: 1.25,
      minNosing: 0.75
    },
    deck: {
      name: 'Deck/Exterior',
      maxRise: 7.75,
      minRun: 10,
      minHeadroom: 80,
      minWidth: 36,
      maxNosing: 1.5,
      minNosing: 0.75
    }
  };

  useEffect(() => {
    if (totalRise) {
      const rise = parseFloat(totalRise);
      const width = parseFloat(stairWidth);
      const code = codeRequirements[codeType];
      
      // Calculate optimal number of risers
      // Start with code max rise and adjust
      let numRisers = Math.ceil(rise / code.maxRise);
      let riserHeight = rise / numRisers;
      
      // Adjust if riser is too tall
      while (riserHeight > code.maxRise && numRisers < 50) {
        numRisers++;
        riserHeight = rise / numRisers;
      }
      
      // Calculate run using the 2R + T = 24-25 rule (comfort formula)
      // Where R = rise, T = tread depth
      let treadDepth = 25 - (2 * riserHeight);
      
      // Ensure minimum tread depth per code
      if (treadDepth < code.minRun) {
        treadDepth = code.minRun;
      }
      
      // Number of treads is risers - 1 (top tread is floor)
      const numTreads = numRisers - 1;
      
      // Total run (horizontal distance)
      const totalRun = numTreads * treadDepth;
      
      // Stringer length (hypotenuse)
      const stringerLength = Math.sqrt((rise * rise) + (totalRun * totalRun));
      
      // Stair angle
      const angleRad = Math.atan(rise / totalRun);
      const angleDeg = angleRad * (180 / Math.PI);
      
      // Code compliance checks
      const compliance = {
        riseOk: riserHeight <= code.maxRise,
        runOk: treadDepth >= code.minRun,
        widthOk: width >= code.minWidth,
        angleOk: angleDeg >= 30 && angleDeg <= 50, // Comfortable range
        ratio: (2 * riserHeight) + treadDepth // Should be 24-25
      };
      compliance.ratioOk = compliance.ratio >= 24 && compliance.ratio <= 25.5;
      compliance.allPass = compliance.riseOk && compliance.runOk && compliance.widthOk;
      
      // Material estimates
      // Stringers: typically 3 for up to 36", 4 for wider
      const stringerCount = width <= 36 ? 3 : width <= 48 ? 4 : 5;
      
      // Stringer board: 2x12 is standard, length = stringer length + 12" for cuts
      const stringerBoardLength = Math.ceil((stringerLength + 12) / 12); // In feet
      
      // Treads: 2 boards (2x6 or 5/4x6) per tread for standard width
      const treadBoards = numTreads * 2;
      
      // Risers: 1x8 boards
      const riserBoards = numRisers;
      
      // Nosing (optional trim piece)
      const nosingLF = numTreads * (width / 12);
      
      // Hardware
      const screwsPerTread = 8; // 4 per board
      const screwsPerRiser = 6;
      const totalScrews = (numTreads * screwsPerTread) + (numRisers * screwsPerRiser);
      const stringerBrackets = stringerCount * 2; // Top and bottom
      
      setResult({
        // Dimensions
        numRisers,
        numTreads,
        riserHeight: riserHeight.toFixed(2),
        treadDepth: treadDepth.toFixed(2),
        totalRun: totalRun.toFixed(1),
        stringerLength: stringerLength.toFixed(1),
        angleDeg: angleDeg.toFixed(1),
        // Code compliance
        compliance,
        code,
        // Materials
        materials: {
          stringerCount,
          stringerBoardLength,
          treadBoards,
          riserBoards,
          nosingLF: Math.ceil(nosingLF),
          totalScrews,
          stringerBrackets
        }
      });
    } else {
      setResult(null);
    }
  }, [totalRise, stairWidth, codeType]);

  return (
    <div className="space-y-2">
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Building Code</Label>
        <Select value={codeType} onValueChange={setCodeType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="residential">Residential (IRC)</SelectItem>
            <SelectItem value="commercial">Commercial (IBC)</SelectItem>
            <SelectItem value="deck">Deck/Exterior</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Total Rise (in)</Label>
          <Input type="number" step="0.5" value={totalRise} onChange={(e) => setTotalRise(e.target.value)}
            placeholder="e.g., 108"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          <p className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
            Floor to floor height
          </p>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Stair Width (in)</Label>
          <Input type="number" step="1" value={stairWidth} onChange={(e) => setStairWidth(e.target.value)}
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          <p className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
            Min: {codeRequirements[codeType].minWidth}"
          </p>
        </div>
      </div>
      
      {result && (
        <div className={`p-2 rounded-lg space-y-2 ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {/* Compliance Status */}
          <div className={`text-center pb-2 border-b ${result.compliance.allPass ? 'border-green-500/30' : 'border-red-500/30'}`}>
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              result.compliance.allPass 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              {result.compliance.allPass ? '✓ Code Compliant' : '⚠ Check Code'}
            </div>
          </div>
          
          {/* Key Dimensions */}
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Risers</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.numRisers}</p>
              <p className={`text-[10px] ${result.compliance.riseOk ? 'text-green-500' : 'text-red-500'}`}>
                {result.riserHeight}" each
              </p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Treads</p>
              <p className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-[#70170F]'}`}>{result.numTreads}</p>
              <p className={`text-[10px] ${result.compliance.runOk ? 'text-green-500' : 'text-red-500'}`}>
                {result.treadDepth}" deep
              </p>
            </div>
          </div>
          
          {/* Stair Geometry */}
          <div>
            <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>GEOMETRY</p>
            <div className="grid grid-cols-3 gap-1 text-xs">
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Run:</span>
                <span className={`ml-1 font-medium ${darkMode ? 'text-white' : ''}`}>{result.totalRun}"</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Angle:</span>
                <span className={`ml-1 font-medium ${darkMode ? 'text-white' : ''}`}>{result.angleDeg}°</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>Stringer:</span>
                <span className={`ml-1 font-medium ${darkMode ? 'text-white' : ''}`}>{result.stringerLength}"</span>
              </div>
            </div>
          </div>
          
          {/* Comfort Formula */}
          <div className={`text-xs p-1.5 rounded ${
            result.compliance.ratioOk 
              ? (darkMode ? 'bg-green-900/20' : 'bg-green-50') 
              : (darkMode ? 'bg-amber-900/20' : 'bg-amber-50')
          }`}>
            <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>2R + T = </span>
            <span className={`font-mono font-bold ${result.compliance.ratioOk ? 'text-green-600' : 'text-amber-600'}`}>
              {result.compliance.ratio.toFixed(1)}
            </span>
            <span className={`ml-1 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
              (ideal: 24-25)
            </span>
          </div>
          
          {/* Materials */}
          <div>
            <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>MATERIALS</p>
            <div className="space-y-0.5 text-xs">
              <p className={darkMode ? 'text-gray-300' : ''}>
                Stringers: {result.materials.stringerCount} × 2x12 @ {result.materials.stringerBoardLength}ft
              </p>
              <p className={darkMode ? 'text-gray-300' : ''}>
                Treads: {result.materials.treadBoards} boards (5/4x6 or 2x6)
              </p>
              <p className={darkMode ? 'text-gray-300' : ''}>
                Risers: {result.materials.riserBoards} × 1x8
              </p>
              <p className={darkMode ? 'text-gray-300' : ''}>
                Hardware: ~{result.materials.totalScrews} screws, {result.materials.stringerBrackets} brackets
              </p>
            </div>
          </div>
          
          {/* Code Reference */}
          <div className={`text-[10px] pt-1 border-t ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-muted-foreground'}`}>
            {result.code.name}: Max rise {result.code.maxRise}", min run {result.code.minRun}", min width {result.code.minWidth}"
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// LVL BEAM SIZING CALCULATOR
// ============================================

function LVLBeamCalc({ darkMode }) {
  const [span, setSpan] = useState('');
  const [loadType, setLoadType] = useState('floor');
  const [tributary, setTributary] = useState('');
  const [plies, setPlies] = useState('2');
  const [result, setResult] = useState(null);

  // LVL Beam Span Data (conservative estimates based on Weyerhaeuser/Boise data)
  // Format: { depth: { loadType: maxTributaryForSpan } }
  const lvlData = {
    // Single ply 1.75" LVL
    single: {
      '5.5': { floor: 6, roof: 8, header: 10 },
      '7.25': { floor: 8, roof: 10, header: 12 },
      '9.25': { floor: 10, roof: 12, header: 14 },
      '9.5': { floor: 10, roof: 12, header: 14 },
      '11.25': { floor: 12, roof: 14, header: 16 },
      '11.875': { floor: 12, roof: 16, header: 18 },
    },
    // Double ply 3.5" LVL
    double: {
      '5.5': { floor: 8, roof: 10, header: 12 },
      '7.25': { floor: 10, roof: 12, header: 14 },
      '9.25': { floor: 12, roof: 14, header: 16 },
      '9.5': { floor: 12, roof: 14, header: 16 },
      '11.25': { floor: 14, roof: 16, header: 18 },
      '11.875': { floor: 16, roof: 18, header: 20 },
      '14': { floor: 18, roof: 20, header: 22 },
      '16': { floor: 20, roof: 22, header: 24 },
    },
    // Triple ply 5.25" LVL
    triple: {
      '7.25': { floor: 12, roof: 14, header: 16 },
      '9.25': { floor: 14, roof: 16, header: 18 },
      '9.5': { floor: 14, roof: 16, header: 18 },
      '11.25': { floor: 16, roof: 18, header: 20 },
      '11.875': { floor: 18, roof: 20, header: 22 },
      '14': { floor: 20, roof: 22, header: 24 },
      '16': { floor: 22, roof: 24, header: 26 },
      '18': { floor: 24, roof: 26, header: 28 },
    }
  };

  useEffect(() => {
    const spanVal = parseFloat(span);
    const tribVal = parseFloat(tributary);
    
    if (spanVal > 0 && tribVal > 0) {
      const plyKey = plies === '1' ? 'single' : plies === '2' ? 'double' : 'triple';
      const plyData = lvlData[plyKey];
      const plyWidth = plies === '1' ? '1¾"' : plies === '2' ? '3½"' : '5¼"';
      
      // Find smallest depth that works
      let recommendations = [];
      const depths = Object.keys(plyData).map(d => parseFloat(d)).sort((a, b) => a - b);
      
      for (const depthStr of Object.keys(plyData)) {
        const depth = parseFloat(depthStr);
        const maxSpan = plyData[depthStr][loadType];
        
        if (maxSpan >= spanVal) {
          // Check if tributary is reasonable (simplified)
          const effectiveTrib = tribVal <= 16 ? 'OK' : tribVal <= 24 ? 'Check' : 'Verify';
          recommendations.push({
            depth: depthStr,
            depthLabel: depthStr.includes('.') ? `${depthStr}"` : `${depthStr}"`,
            maxSpan,
            tributaryStatus: effectiveTrib,
            size: `${plyWidth} × ${depthStr}"`
          });
        }
      }
      
      setResult({
        span: spanVal,
        tributary: tribVal,
        loadType,
        plies,
        plyWidth,
        recommendations: recommendations.slice(0, 4), // Show up to 4 options
        note: recommendations.length === 0 ? 'Span may exceed typical LVL capacity. Consider Glulam or PSL.' : null
      });
    } else {
      setResult(null);
    }
  }, [span, loadType, tributary, plies]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Span (ft)</Label>
          <Input type="number" step="0.5" value={span} onChange={(e) => setSpan(e.target.value)}
            placeholder="e.g., 12"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Tributary (ft)</Label>
          <Input type="number" step="0.5" value={tributary} onChange={(e) => setTributary(e.target.value)}
            placeholder="e.g., 8"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
          <p className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
            Load width
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Load Type</Label>
          <Select value={loadType} onValueChange={setLoadType}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="floor">Floor (40 psf)</SelectItem>
              <SelectItem value="roof">Roof (30 psf)</SelectItem>
              <SelectItem value="header">Header/Opening</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Plies</Label>
          <Select value={plies} onValueChange={setPlies}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Single (1¾")</SelectItem>
              <SelectItem value="2">Double (3½")</SelectItem>
              <SelectItem value="3">Triple (5¼")</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {result && (
        <div className={`p-2 rounded-lg space-y-2 ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {result.recommendations.length > 0 ? (
            <>
              <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                RECOMMENDED SIZES ({result.plyWidth} width)
              </p>
              <div className="space-y-1">
                {result.recommendations.map((rec, i) => (
                  <div key={i} className={`flex justify-between items-center text-xs p-1.5 rounded ${
                    i === 0 
                      ? (darkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200')
                      : ''
                  }`}>
                    <span className={`font-mono font-medium ${darkMode ? 'text-white' : ''}`}>
                      {rec.size}
                    </span>
                    <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>
                      max {rec.maxSpan}' span
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={`text-center py-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              <p className="text-xs font-medium">⚠️ {result.note}</p>
            </div>
          )}
          
          <div className={`text-[9px] pt-1 border-t ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-muted-foreground'}`}>
            ⚠️ Preliminary only. Verify with manufacturer tables & engineer.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// I-JOIST SPAN CALCULATOR
// ============================================

function IJoistSpanCalc({ darkMode }) {
  const [span, setSpan] = useState('');
  const [spacing, setSpacing] = useState('16');
  const [loadType, setLoadType] = useState('floor');
  const [result, setResult] = useState(null);

  // I-Joist span data (based on TJI/BCI specifications, L/360 deflection)
  // Format: depth → series → spacing → max span
  const iJoistData = {
    '9.5': {
      '110': { '12': 17.5, '16': 16.0, '19.2': 15.5, '24': 14.0 },
      '210': { '12': 19.0, '16': 17.5, '19.2': 16.5, '24': 15.0 },
      '230': { '12': 21.0, '16': 19.5, '19.2': 18.5, '24': 17.0 },
    },
    '11.875': {
      '210': { '12': 23.0, '16': 21.5, '19.2': 20.5, '24': 18.5 },
      '230': { '12': 25.5, '16': 24.0, '19.2': 22.5, '24': 20.5 },
      '360': { '12': 27.5, '16': 26.0, '19.2': 24.5, '24': 22.5 },
    },
    '14': {
      '230': { '12': 27.0, '16': 25.5, '19.2': 24.0, '24': 22.0 },
      '360': { '12': 29.0, '16': 27.5, '19.2': 26.0, '24': 24.0 },
      '560': { '12': 30.0, '16': 28.5, '19.2': 27.0, '24': 25.0 },
    },
    '16': {
      '360': { '12': 31.0, '16': 29.5, '19.2': 28.0, '24': 26.0 },
      '560': { '12': 32.0, '16': 30.5, '19.2': 29.0, '24': 27.0 },
    }
  };

  useEffect(() => {
    const spanVal = parseFloat(span);
    
    if (spanVal > 0) {
      let options = [];
      
      // Check each depth/series combination
      for (const [depth, seriesData] of Object.entries(iJoistData)) {
        for (const [series, spacingData] of Object.entries(seriesData)) {
          const maxSpan = spacingData[spacing];
          if (maxSpan && maxSpan >= spanVal) {
            options.push({
              depth,
              series,
              maxSpan,
              margin: ((maxSpan - spanVal) / spanVal * 100).toFixed(0)
            });
          }
        }
      }
      
      // Sort by depth (smallest first), then by series
      options.sort((a, b) => {
        const depthDiff = parseFloat(a.depth) - parseFloat(b.depth);
        if (depthDiff !== 0) return depthDiff;
        return parseInt(a.series) - parseInt(b.series);
      });
      
      setResult({
        span: spanVal,
        spacing,
        options: options.slice(0, 5),
        note: options.length === 0 ? 'Consider reducing spacing or using deeper joists.' : null
      });
    } else {
      setResult(null);
    }
  }, [span, spacing, loadType]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Span (ft)</Label>
          <Input type="number" step="0.5" value={span} onChange={(e) => setSpan(e.target.value)}
            placeholder="e.g., 18"
            className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`} />
        </div>
        <div>
          <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Spacing (o.c.)</Label>
          <Select value={spacing} onValueChange={setSpacing}>
            <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12" o.c.</SelectItem>
              <SelectItem value="16">16" o.c.</SelectItem>
              <SelectItem value="19.2">19.2" o.c.</SelectItem>
              <SelectItem value="24">24" o.c.</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className={`text-xs ${darkMode ? 'text-gray-300' : ''}`}>Application</Label>
        <Select value={loadType} onValueChange={setLoadType}>
          <SelectTrigger className={`h-8 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#3A3A3A] text-white' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="floor">Floor Joist (40/10 psf)</SelectItem>
            <SelectItem value="ceiling">Ceiling Joist (10/5 psf)</SelectItem>
          </SelectContent>
        </Select>
        <p className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
          L/360 live load deflection
        </p>
      </div>

      {result && (
        <div className={`p-2 rounded-lg space-y-2 ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
          {result.options.length > 0 ? (
            <>
              <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                OPTIONS FOR {result.span}' SPAN @ {result.spacing}" O.C.
              </p>
              <div className="space-y-1">
                {result.options.map((opt, i) => (
                  <div key={i} className={`flex justify-between items-center text-xs p-1.5 rounded ${
                    i === 0 
                      ? (darkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200')
                      : ''
                  }`}>
                    <span className={`font-mono font-medium ${darkMode ? 'text-white' : ''}`}>
                      {opt.depth}" TJI {opt.series}
                    </span>
                    <div className="text-right">
                      <span className={`text-green-500 text-[10px]`}>+{opt.margin}%</span>
                      <span className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                        max {opt.maxSpan}'
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className={`text-[10px] pt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                <span className="font-medium">Series Guide:</span> 110/210 = economy, 230 = standard, 360/560 = premium
              </div>
            </>
          ) : (
            <div className={`text-center py-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              <p className="text-xs font-medium">⚠️ {result.note}</p>
            </div>
          )}
          
          <div className={`text-[9px] pt-1 border-t ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-muted-foreground'}`}>
            ⚠️ Preliminary only. Verify with manufacturer tables.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ENGINEERED WOOD REFERENCE
// ============================================

function EngWoodReference({ darkMode }) {
  const products = [
    { name: 'LVL', full: 'Laminated Veneer Lumber', use: 'Beams, headers, rim board', brands: 'Microllam®, Versa-Lam®' },
    { name: 'Glulam', full: 'Glued Laminated Timber', use: 'Beams, columns, arches', brands: 'Multiple APA members' },
    { name: 'I-Joist', full: 'Prefabricated I-Joist', use: 'Floor/roof joists', brands: 'TJI®, BCI®, GP' },
    { name: 'PSL', full: 'Parallel Strand Lumber', use: 'Heavy beams, columns', brands: 'Parallam®' },
    { name: 'LSL', full: 'Laminated Strand Lumber', use: 'Headers, studs, rim', brands: 'TimberStrand®' },
  ];

  const manufacturers = [
    { name: 'Weyerhaeuser', products: 'TJI, Microllam, Parallam, TimberStrand', software: 'ForteWEB®' },
    { name: 'Boise Cascade', products: 'BCI, Versa-Lam, ALLJOIST', software: 'BC Calc®' },
    { name: 'Anthony Forest', products: 'LVL, Glulam, I-Joists', software: 'Framing Designer' },
    { name: 'Huber', products: 'AdvanTech, ZIP System', software: 'AdvanTech App' },
  ];

  return (
    <div className="space-y-2">
      <p className={`text-[10px] font-semibold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
        ENGINEERED WOOD PRODUCTS
      </p>
      <div className="space-y-1.5">
        {products.map((p, i) => (
          <div key={i} className={`p-1.5 rounded text-xs ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-50'}`}>
            <div className="flex justify-between">
              <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{p.name}</span>
              <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{p.full}</span>
            </div>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              {p.use} • {p.brands}
            </p>
          </div>
        ))}
      </div>
      
      <p className={`text-[10px] font-semibold mt-3 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
        MANUFACTURERS & SOFTWARE
      </p>
      <div className="space-y-1">
        {manufacturers.map((m, i) => (
          <div key={i} className={`flex justify-between text-[10px] ${darkMode ? 'text-gray-300' : ''}`}>
            <span className="font-medium">{m.name}</span>
            <span className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>{m.software}</span>
          </div>
        ))}
      </div>
      
      <div className={`text-[9px] pt-2 border-t ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-muted-foreground'}`}>
        💡 Use Design Companion → Structural Materials for project-specific guidance with notes.
      </div>
    </div>
  );
}

// ============================================
// CALCULATOR LIST
// ============================================

const CALCULATORS = [
  // Conversion
  { id: 'fraction', name: 'Decimal → Fraction', icon: Hash, component: DecimalToFraction, category: 'Conversion', featured: true },
  { id: 'inches', name: 'Inches → Metric', icon: Ruler, component: InchesConverter, category: 'Conversion' },
  
  // Concrete
  { id: 'concrete', name: 'Concrete Volume', icon: Box, component: ConcreteCalculator, category: 'Concrete' },
  { id: 'block', name: 'Concrete Block', icon: Grid3X3, component: BlockCalculator, category: 'Concrete' },
  { id: 'brick', name: 'Brick', icon: Grid3X3, component: BrickCalculator, category: 'Concrete' },
  { id: 'rebar', name: 'Rebar Estimator', icon: Grid3X3, component: RebarEstimator, category: 'Concrete' },
  { id: 'footing', name: 'Concrete Footing', icon: Box, component: FootingCalculator, category: 'Concrete' },
  
  // Framing
  { id: 'boardfoot', name: 'Board Feet', icon: Ruler, component: BoardFootCalculator, category: 'Framing' },
  { id: 'stud', name: 'Stud Framing', icon: Home, component: StudCalculator, category: 'Framing' },
  { id: 'beamspan', name: 'Beam/Span Guide', icon: ArrowLeftRight, component: BeamSpanCalc, category: 'Framing' },
  { id: 'stairs', name: 'Stair Calculator', icon: Footprints, component: StairCalculator, category: 'Framing', featured: true },
  
  // Engineered Wood (NEW)
  { id: 'lvlbeam', name: 'LVL Beam Sizing', icon: Layers, component: LVLBeamCalc, category: 'Engineered Wood', featured: true },
  { id: 'ijoist', name: 'I-Joist Spans', icon: Layers, component: IJoistSpanCalc, category: 'Engineered Wood', featured: true },
  { id: 'engwood', name: 'EWP Reference', icon: Layers, component: EngWoodReference, category: 'Engineered Wood' },
  
  // Exterior
  { id: 'roofpitch', name: 'Roof Pitch', icon: Layers, component: RoofPitchCalculator, category: 'Exterior' },
  { id: 'roofing', name: 'Roofing Materials', icon: Layers, component: RoofingCalculator, category: 'Exterior' },
  { id: 'shingles', name: 'Shingle Count', icon: Layers, component: ShingleCalculator, category: 'Exterior' },
  { id: 'gutter', name: 'Gutter Sizing', icon: Droplets, component: GutterSizingCalc, category: 'Exterior' },
  { id: 'deck', name: 'Deck Materials', icon: Fence, component: DeckMaterialsCalc, category: 'Exterior', featured: true },
  
  // Estimating
  { id: 'drywall', name: 'Drywall', icon: Layers, component: DrywallCalculator, category: 'Estimating' },
  { id: 'paint', name: 'Paint Coverage', icon: PaintBucket, component: PaintCalculator, category: 'Estimating' },
  { id: 'excavation', name: 'Excavation Volume', icon: Shovel, component: ExcavationCalc, category: 'Estimating' },
  { id: 'waste', name: 'Material Waste', icon: Package, component: MaterialWasteCalc, category: 'Estimating' },
  { id: 'fastener', name: 'Nail/Fastener', icon: Hammer, component: FastenerCalc, category: 'Estimating', featured: true },
  
  // Architecture
  { id: 'roomarea', name: 'Room Area', icon: Square, component: RoomAreaCalculator, category: 'Architecture', featured: true },
  { id: 'windowfloor', name: 'Window/Floor Ratio', icon: Square, component: WindowFloorRatio, category: 'Architecture' },
  { id: 'ceiling', name: 'Ceiling Heights', icon: ArrowLeftRight, component: CeilingHeightCalc, category: 'Architecture' },
  { id: 'ada', name: 'ADA Clearances', icon: Accessibility, component: ADAClearanceChecker, category: 'Architecture' },
  
  // Interior Design
  { id: 'wallpaper', name: 'Wallpaper', icon: Layers, component: WallpaperCalculator, category: 'Interior Design' },
  { id: 'rugsize', name: 'Rug Size Guide', icon: Square, component: RugSizeGuide, category: 'Interior Design' },
  { id: 'furniture', name: 'Furniture Scale', icon: Sofa, component: FurnitureScaleCalc, category: 'Interior Design' },
  { id: 'colortemp', name: 'Color Temperature', icon: Palette, component: ColorTempReference, category: 'Interior Design' },
  { id: 'fabric', name: 'Fabric Yardage', icon: Scissors, component: FabricYardageCalc, category: 'Interior Design' },
  
  // Engineering
  { id: 'snowload', name: 'Snow Load', icon: Thermometer, component: SnowLoadCalc, category: 'Engineering' },
  { id: 'windload', name: 'Wind Load', icon: Wind, component: WindLoadReference, category: 'Engineering' },
  { id: 'seismic', name: 'Seismic Zone', icon: Mountain, component: SeismicZoneRef, category: 'Engineering' },
  
  // Reference
  { id: 'leadtime', name: 'Lead Times', icon: Clock, component: LeadTimeReference, category: 'Reference' },
  { id: 'permitfee', name: 'Permit Fee Est.', icon: FileText, component: PermitFeeEstimator, category: 'Reference' },
  
  // Financial
  { id: 'markup', name: 'Markup & Margin', icon: DollarSign, component: MarkupMarginCalculator, category: 'Financial', featured: true },
  { id: 'labor', name: 'Labor Burden', icon: DollarSign, component: LaborBurdenCalculator, category: 'Financial' },
  { id: 'overhead', name: 'Overhead Allocation', icon: DollarSign, component: OverheadAllocationCalculator, category: 'Financial' },
  { id: 'breakeven', name: 'Break-Even', icon: DollarSign, component: BreakEvenCalculator, category: 'Financial' },
  { id: 'retainage', name: 'Retainage', icon: DollarSign, component: RetainageCalculator, category: 'Financial' },
  { id: 'costsqft', name: 'Cost per SF', icon: DollarSign, component: CostPerSqFtCalculator, category: 'Financial' },
  { id: 'escalation', name: 'Escalation Rate', icon: DollarSign, component: EscalationCalculator, category: 'Financial' },
];

// ============================================
// MAIN CALCULATOR PANEL COMPONENT
// ============================================

export default function CalculatorPanel({ isOpen, onClose, darkMode = false }) {
  const [openCalculators, setOpenCalculators] = useState([]);
  const [cardPositions, setCardPositions] = useState({});
  const [userFavorites, setUserFavorites] = useState(() => {
    // Load favorites from localStorage on init
    const saved = localStorage.getItem('keymade_calc_favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('keymade_calc_favorites', JSON.stringify(userFavorites));
  }, [userFavorites]);

  const toggleFavorite = (calcId, e) => {
    e.stopPropagation(); // Prevent opening the calculator
    setUserFavorites(prev => 
      prev.includes(calcId) 
        ? prev.filter(id => id !== calcId)
        : [...prev, calcId]
    );
  };

  const isFavorite = (calcId) => userFavorites.includes(calcId);

  const openCalculator = (calc) => {
    if (openCalculators.find(c => c.id === calc.id)) return;
    
    // Calculate position for new card (stagger them)
    const offset = openCalculators.length * 30;
    const newPosition = { x: 200 + offset, y: 100 + offset };
    
    setCardPositions(prev => ({ ...prev, [calc.id]: newPosition }));
    setOpenCalculators(prev => [...prev, calc]);
  };

  const closeCalculator = (calcId) => {
    setOpenCalculators(prev => prev.filter(c => c.id !== calcId));
  };

  if (!isOpen) return null;

  // Filter calculators based on favorites toggle
  const filteredCalculators = showFavoritesOnly 
    ? CALCULATORS.filter(c => userFavorites.includes(c.id))
    : CALCULATORS;

  // Group calculators by category
  const categories = [...new Set(filteredCalculators.map(c => c.category))];

  return (
    <>
      {/* Calculator List Panel */}
      <div 
        className={`fixed right-0 h-full w-56 z-40 shadow-xl transition-transform duration-300 ${
          darkMode ? 'bg-[#0D0D0D] border-l border-[#2A2A2A]' : 'bg-white border-l'
        }`}
        style={{ top: '60px', height: 'calc(100vh - 60px)' }}
        data-testid="calculator-list"
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#BC5D41]" />
            <h2 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>Calculators</h2>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Favorites Filter Toggle */}
        <div className={`px-3 py-2 border-b ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-100'}`}>
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs transition-colors ${
              showFavoritesOnly
                ? 'bg-amber-500/20 text-amber-500 ring-1 ring-amber-500/50'
                : darkMode
                  ? 'hover:bg-[#1A1A1A] text-gray-400'
                  : 'hover:bg-gray-100 text-gray-500'
            }`}
            data-testid="favorites-filter-btn"
          >
            <Star className={`w-3.5 h-3.5 ${showFavoritesOnly ? 'fill-amber-500' : ''}`} />
            <span>Show Favorites Only</span>
            {userFavorites.length > 0 && (
              <Badge className={`ml-auto text-[9px] px-1.5 py-0 ${
                showFavoritesOnly ? 'bg-amber-500' : 'bg-gray-400'
              }`}>
                {userFavorites.length}
              </Badge>
            )}
          </button>
        </div>

        {/* Calculator List */}
        <ScrollArea className="h-[calc(100vh-160px)]">
          <div className="p-2">
            {categories.length === 0 && showFavoritesOnly ? (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                <Star className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No favorites yet</p>
                <p className="text-[10px] mt-1">Click the ★ on any calculator to add it</p>
              </div>
            ) : (
              categories.map(category => (
                <div key={category} className="mb-3">
                  <p className={`text-[10px] font-medium uppercase tracking-wider mb-1 px-2 ${
                    darkMode ? 'text-gray-500' : 'text-muted-foreground'
                  }`}>
                    {category}
                  </p>
                  <div className="space-y-0.5">
                    {filteredCalculators.filter(c => c.category === category).map(calc => {
                      const isOpen = openCalculators.find(c => c.id === calc.id);
                      const isFav = isFavorite(calc.id);
                      return (
                        <button
                          key={calc.id}
                          onClick={() => openCalculator(calc)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors group ${
                            isOpen
                              ? 'bg-[#BC5D41] text-white'
                              : darkMode 
                                ? 'hover:bg-[#1A1A1A] text-gray-300' 
                                : 'hover:bg-gray-100'
                          } ${isFav ? 'ring-1 ring-amber-400/50' : ''}`}
                          data-testid={`calc-btn-${calc.id}`}
                        >
                          <calc.icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{calc.name}</span>
                          {/* Favorite Star - Clickable */}
                          <span
                            onClick={(e) => toggleFavorite(calc.id, e)}
                            className={`ml-auto p-0.5 rounded transition-colors ${
                              isFav 
                                ? 'text-amber-500 hover:text-amber-400' 
                                : isOpen
                                  ? 'text-white/50 hover:text-white'
                                  : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:text-amber-500'
                            }`}
                            title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                            data-testid={`calc-fav-${calc.id}`}
                          >
                            <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-amber-500' : ''}`} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Floating Calculator Cards */}
      {openCalculators.map(calc => {
        const CalcComponent = calc.component;
        return (
          <DraggableCard
            key={calc.id}
            id={calc.id}
            title={calc.name}
            icon={calc.icon}
            onClose={() => closeCalculator(calc.id)}
            darkMode={darkMode}
            initialPosition={cardPositions[calc.id]}
          >
            <CalcComponent darkMode={darkMode} />
          </DraggableCard>
        );
      })}
    </>
  );
}
